import { useCallback, useEffect, useRef, useState } from "react";
import {
  PawPrint,
  Building2,
  Users,
  BedDouble,
  CalendarDays,
  Car,
  Check,
  ChevronRight,
  FileText,
  Handshake,
  Heart,
  House,
  Info,
  LayoutGrid,
  MapPin,
  type LucideIcon,
  Mail,
  Minus,
  Pencil,
  Plus,
  Shield,
  ShoppingCart,
  Stethoscope,
  Sun,
  Tag,
  Truck,
  Undo2,
  User,
} from "lucide-react";
import { useCustom, useCustomMutation, useGetLocale, usePermissions, useTranslate, useUpdate } from "@refinedev/core";
import {
  DateField,
  ToggleField,
  SelectField,
  Button,
  DownloadIcon,
  UploadCloudIcon,
  Field,
  Modal,
  type FieldProps,
  FieldShell,
  FileIcon,
  ReadField,
  SearchIcon,
  StatusBadge,
  Tile,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { useModuleAvailability } from "../api/modules";
import { formatDate, formatDateTime, formatEuro, todayIso } from "../i18n/format";
import {
  deleteRelatieMedia,
  downloadRelatieMedia,
  fetchRelatieMediaBlob,
  uploadRelatieMedia,
} from "../api/relatieMedia";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { BlokkadeBanner } from "./blokkadeBanner";
import {
  bestandsSoort, binnenMediaPeriode, ImageGlyph, isImage, isVideo, kaartDatum, MEDIA_DATUM_FILTERS, MEDIA_OP_TEGEL,
  soortKleur, tagChipStyle, UploadDialog, VideoGlyph, type MediaDatumFilter,
} from "./animalTiles";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Relaties-detail migration (Jmix-exit), Phase 1: the per-tile content components for the relation
// detail — the customer counterpart to animalTiles. Each fetches its own read sub-resource
// (GET /api/relaties/{id}/<sub>) via Refine's useCustom (relative url — the dataProvider prepends
// /api) and renders that Jmix fragment's fields read-only. Writes/dialogs are a deferred follow-up.

export interface RelatieRolView {
  code: string;
  label: string;
  icoon: string | null;
}

export interface RelatieTagView {
  naam: string;
  kleur: string | null;
}

// The detail payload behind the identity header + Persoonsgegevens/Bedrijfsgegevens tile.
export interface RelatieDetail {
  id: number;
  klantNummer: number | null;
  klantnaam: string | null;
  organisatie: boolean;
  actief: boolean;
  version: number | null;
  aanhef: string | null;
  voorletters: string | null;
  voornaam: string | null;
  tussenvoegsel: string | null;
  achternaam: string | null;
  geboortedatum: string | null;
  naamBedrijf: string | null;
  kvkNummer: string | null;
  btwNummer: string | null;
  emailAdres: string | null;
  telefoon1: string | null;
  telefoon2: string | null;
  straat: string | null;
  huisnummer: string | null;
  huisnummerToevoeging: string | null;
  postcode: string | null;
  woonplaats: string | null;
  land: string | null;
  roles: RelatieRolView[];
  tags: RelatieTagView[];
  roleIds: number[];
  tagIds: number[];
  /** Naar welke relatie deze is samengevoegd; leeg bij een gewone relatie. */
  samengevoegdMetKlantId?: number | null;
}

interface RelatieEvent {
  id: number;
  eventType: string;
  payload: string | null;
  userId: string | null;
  occurredAt: string | null;
}

interface FactuurRegel {
  id: number;
  nummer: string;
  factuurDatum: string | null;
  vervalDatum: string | null;
  status: "CONCEPT" | "BETAALD" | "OPENSTAAND" | "VERVALLEN";
  totaal: number | null;
  betaald: number | null;
  openstaand: number | null;
  /** Which module raised it — ASIEL / PENSION / CRECHE / LOSSE_VERKOOP / OVERIG (the tile's filter). */
  module: string | null;
}

interface FacturenTileData {
  regels: FactuurRegel[];
  totaalOpenstaand: number;
  totaalBetaald: number;
  totaalVervallen: number;
  aantalOpenstaand: number;
  aantalBetaald: number;
  aantalVervallen: number;
  aantalTotaal: number;
}

interface VerblijfRegel {
  id: number;
  dierId: number | null;
  dierNaam: string | null;
  diersoort: string | null;
  rol: string | null;
  vestiging: string | null;
  opnameSoort: string | null;
  opnameDatum: string | null;
  vertrekDatum: string | null;
  aantalDagen: number;
  status: "ACTIEF" | "AFGEROND";
}

interface VerblijvenTileData {
  regels: VerblijfRegel[];
  aantalActief: number;
  aantalTotaal: number;
  laatsteVertrek: string | null;
}

interface PleeggezinCapaciteit {
  diersoortId: number | null;
  diersoort: string | null;
  maxCapaciteit: number;
  huidig: number;
  vrij: number;
}

// The editable capaciteit/welkom catalog (GET .../pleeggezin/capaciteit-catalog), scoped to the
// operator's current vestiging. vestigingId null → no current shelter (read-only hint).
interface CatalogCategorie {
  opvangcategorieId: number;
  naam: string | null;
  welkom: boolean;
}
interface CatalogDiersoort {
  diersoortId: number;
  naam: string | null;
  maxCapaciteit: number;
  categorieen: CatalogCategorie[];
}
interface CapaciteitCatalog {
  vestigingId: number | null;
  diersoorten: CatalogDiersoort[];
}

interface PleeggezinProfielData {
  heeftProfiel: boolean;
  klantUuid: string | null;
  loginActief: boolean;
  loginEmail: string | null;
  interneOpmerking: string | null;
  laatsteLogin: string | null;
  capaciteit: PleeggezinCapaciteit[];
  welkomCategorieen: string[];
}

/** Whole days between an intake date and today — the "132 dagen in opvang" line Jmix shows. */
function dagenSinds(datum: string | null): number | null {
  if (!datum) return null;
  const dan = Date.parse(datum);
  if (Number.isNaN(dan)) return null;
  return Math.max(0, Math.floor((Date.now() - dan) / 86_400_000));
}

/** One relation found at the same address as the one being edited. */
interface RelatieOpAdres {
  id: number;
  klantNummer: number | null;
  klantnaam: string | null;
  woonplaats: string | null;
}

/** One row of "Dier plaatsen": an animal this foster family may take in. */
interface PlaatsbaarDier {
  dierId: number;
  dierNaam: string | null;
  diersoort: string | null;
  vestiging: string | null;
  opnameSoort: string | null;
  opnameDatum: string | null;
}

interface PleeggezinPlaatsing {
  id: number;
  dierId: number | null;
  dierNaam: string | null;
  diersoort: string | null;
  categorie: string | null;
  vestiging: string | null;
  opnameSoort: string | null;
  vanDatum: string | null;
  totDatum: string | null;
  aantalDagen: number;
}

interface Afwezigheid {
  id: string;
  van: string | null;
  tot: string | null;
  type: string | null;
  source: string | null;
  aantalDagen: number;
}

interface MediaTagView {
  label: string;
  color: string | null;
}

interface MediaFile {
  id: number;
  fileName: string | null;
  contentType: string | null;
  fileSizeBytes: number | null;
  uploadedAt: string | null;
  categorie: string | null;
  image: boolean;
  previewAvailable: boolean;
  tags: MediaTagView[];
}

function useTile<T>(relatieId: string, sub: string, options?: { retry?: boolean }) {
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useCustom({
    url: `relaties/${relatieId}/${sub}`,
    method: "get",
    // Some tiles need to fail fast: react-query retries 3× with exponential backoff by default, which
    // keeps `isLoading` true (and `isError` false) for ~7s on a server error — long enough that an
    // error state looks like a perpetual spinner. Opting out surfaces `isError` on the first response.
    queryOptions: options?.retry === false ? { retry: false } : undefined,
  });
  return { data: data?.data as T | undefined, isLoading, isError, refetch, dataUpdatedAt };
}

function Status({ isLoading, isError, t }: { isLoading: boolean; isError: boolean; t: (k: string) => string }) {
  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (isError) return <p className="fawkes-error">{t("relatieDetailView.tileLoadFailed")}</p>;
  return null;
}



// ---- Persoonsgegevens / Bedrijfsgegevens -----------------------------------------------------------
// Port of the Jmix PersoonsgegevensTileFragment: identity fields (person vs organisation), the address
// + contact blocks, and the role / tag pickers. Always editable, the way Jmix builds it — no edit mode;
// Opslaan/Annuleren surface once the card is dirty. Mirrors RelatieService.updatePersoon (person) /
// updateOrganisatie (org) + shared adres + contact. Authority (ROLE_RELATIE_EDIT) is backend-enforced;
// a 403 surfaces as a notice (matching the Animal basis-tile).
interface PersoonForm {
  organisatie: boolean;
  aanhef: string;
  voorletters: string;
  tussenvoegsel: string;
  achternaam: string;
  bedrijfsnaam: string;
  kvkNummer: string;
  btwNummer: string;
  straat: string;
  huisnummer: string;
  huisnummerToevoeging: string;
  postcode: string;
  woonplaats: string;
  land: string;
  email: string;
  telefoon1: string;
  telefoon2: string;
  roleIds: number[];
  tagIds: number[];
}

/**
 * A form field occupying more than one grid track.
 *
 * Field spreads anything it does not know onto its <input>, so a className passed to it lands on the
 * input and REPLACES fui-field__input — the field loses its styling and the cell never spans. The
 * span therefore belongs on a wrapper around it, not on the field.
 */
/** The salutations the Jmix picker offers, in the order the data uses them. */
const AANHEF_OPTIES = ["Fam.", "Mevr.", "Dhr.", "Dhr./Mevr.", "Mej.", "t.a.v.", "Anders"];
/** The country codes stored in klant.land — the picker shows the code, which is what is saved. */
/**
 * De landen in de keuzelijst. De code is wat er opgeslagen wordt; de vlag en de naam zijn er voor
 * de lezer — "NL" alleen laat je twee keer kijken of het nu Nederland of Noorwegen is.
 */
const LANDEN = [
  { code: "NL", naam: "Nederland" },
  { code: "BE", naam: "België" },
  { code: "DE", naam: "Duitsland" },
  { code: "FR", naam: "Frankrijk" },
  { code: "GB", naam: "Verenigd Koninkrijk" },
  { code: "LU", naam: "Luxemburg" },
];

/** De banen van elke vlag: horizontaal tenzij anders vermeld. */
const VLAGGEN: Record<string, { banen: string[]; verticaal?: boolean }> = {
  NL: { banen: ["#ae1c28", "#ffffff", "#21468b"] },
  BE: { banen: ["#000000", "#fdda24", "#ef3340"], verticaal: true },
  DE: { banen: ["#000000", "#dd0000", "#ffce00"] },
  FR: { banen: ["#002395", "#ffffff", "#ed2939"], verticaal: true },
  LU: { banen: ["#ed2939", "#ffffff", "#00a1de"] },
  GB: { banen: ["#012169", "#ffffff", "#c8102e"] },
};

/** Een vlaggetje van drie banen; onbekende landen krijgen niets. */
function Landvlag({ code }: { code: string }) {
  const vlag = VLAGGEN[code];
  if (!vlag) return null;
  const verticaal = vlag.verticaal === true;
  return (
    <span className="fawkes-relatie-vlag" aria-hidden="true">
      <svg viewBox="0 0 12 9" width="16" height="12" role="presentation">
        {vlag.banen.map((kleur, i) =>
          verticaal ? (
            <rect key={kleur + i} x={i * 4} y={0} width={4} height={9} fill={kleur} />
          ) : (
            <rect key={kleur + i} x={0} y={i * 3} width={12} height={3} fill={kleur} />
          ),
        )}
      </svg>
    </span>
  );
}
const LAND_OPTIES = LANDEN.map((l) => l.code);

function VeldCel({ span, ...veld }: { span?: 2 | 3 | 4 } & FieldProps) {
  return <div className={span ? `fawkes-relatie-grid__span${span}` : undefined}><Field {...veld} /></div>;
}

/** De landen voor de keuzelijst. Een waarde die al is opgeslagen maar niet in de lijst staat komt er
 *  vooraan bij, anders zou openen en opslaan hem stilletjes wissen. */
/** De aanheffen voor de keuzelijst; een opgeslagen waarde buiten de lijst komt er vooraan bij, zodat
 *  een oude "Mvr." of "Hr." niet stilletjes verdwijnt zodra je de kaart opent en opslaat. */
function aanhefOpties(huidig: string): { value: string; label: string }[] {
  const extra = huidig && !AANHEF_OPTIES.includes(huidig) ? [{ value: huidig, label: huidig }] : [];
  return [{ value: "", label: "—" }, ...extra, ...AANHEF_OPTIES.map((o) => ({ value: o, label: o }))];
}

function landOpties(huidig: string): { value: string; label: string }[] {
  const bekend = LANDEN.map((l) => ({ value: l.code, label: l.naam }));
  // Een opgeslagen code die niet in de lijst staat komt er vooraan bij, anders zou openen en
  // opslaan hem wissen.
  const extra = huidig && !LAND_OPTIES.includes(huidig) ? [{ value: huidig, label: huidig }] : [];
  return [{ value: "", label: "—" }, ...extra, ...bekend];
}

function toPersoonForm(d: RelatieDetail): PersoonForm {
  return {
    organisatie: d.organisatie,
    aanhef: d.aanhef ?? "",
    voorletters: d.voorletters ?? "",
    tussenvoegsel: d.tussenvoegsel ?? "",
    achternaam: d.achternaam ?? "",
    bedrijfsnaam: d.naamBedrijf ?? "",
    kvkNummer: d.kvkNummer ?? "",
    btwNummer: d.btwNummer ?? "",
    straat: d.straat ?? "",
    huisnummer: d.huisnummer ?? "",
    huisnummerToevoeging: d.huisnummerToevoeging ?? "",
    postcode: d.postcode ?? "",
    woonplaats: d.woonplaats ?? "",
    land: d.land ?? "",
    email: d.emailAdres ?? "",
    telefoon1: d.telefoon1 ?? "",
    telefoon2: d.telefoon2 ?? "",
    roleIds: d.roleIds ?? [],
    tagIds: d.tagIds ?? [],
  };
}

type PersoonNotice =
  | "saved" | "conflict" | "forbidden" | "error" | "required" | "requiredBedrijf" | "pleeggezinInUse" | null;

/**
 * The catalog's own icon name → a glyph. `relatie_rol.icoon` carries the name, so a role added to the
 * catalog gets its icon without a code change here; an unknown or absent name falls back to the tag
 * mark rather than rendering nothing.
 */
const ROL_ICONEN: Record<string, LucideIcon> = {
  SHIELD: Shield,
  CAR: Car,
  HEART: Heart,
  STETHOSCOPE: Stethoscope,
  HOME: House,
  TRUCK: Truck,
  HANDSHAKE: Handshake,
};
function RolIcoon({ icoon }: { icoon: string | null | undefined }) {
  const Icon = (icoon && ROL_ICONEN[icoon]) || Tag;
  return <Icon size={18} aria-hidden="true" />;
}

interface RolOptie {
  id: number;
  code: string;
  label: string;
  icoon: string | null;
}

/**
 * "Rol / categorie" + "Tags" — the Jmix pair, as a two-column grid of role cards (icon, label, a check
 * when it applies) over a tags block. One component for both states: pass `onToggleRole` and the cards
 * become buttons, leave it out and they are static. The read view therefore shows the same grid the
 * editor does, so the screen does not rearrange itself the moment you pick up the pencil.
 */
function RolCategorieSectie({
  opties,
  geselecteerd,
  onToggleRole,
  tags,
  onTagToevoegen,
  tagToevoegenLabel,
  t,
}: {
  opties: RolOptie[];
  geselecteerd: (o: RolOptie) => boolean;
  onToggleRole?: (id: number) => void;
  tags: React.ReactNode;
  onTagToevoegen?: () => void;
  tagToevoegenLabel?: string;
  t: (k: string) => string;
}) {
  return (
    <section className="fawkes-relatie-rollen">
      <h5 className="fawkes-relatie-tile__subhead">
        <Tag size={18} aria-hidden="true" />
        {t("relatieDetailView.section.rolCategorie")}
      </h5>
      <p className="fawkes-relatie-rollen__hint">{t("relatieDetailView.section.rolCategorieHint")}</p>
      <div className="fawkes-relatie-rollen__grid">
        {opties.map((o) => {
          const on = geselecteerd(o);
          const inhoud = (
            <>
              <RolIcoon icoon={o.icoon} />
              <span className="fawkes-relatie-rol__label">{o.label}</span>
              {on && <Check className="fawkes-relatie-rol__check" size={16} aria-hidden="true" />}
            </>
          );
          return onToggleRole ? (
            <button
              key={o.id}
              type="button"
              aria-pressed={on}
              className={`fawkes-relatie-rol${on ? " is-on" : ""}`}
              onClick={() => onToggleRole(o.id)}
            >
              {inhoud}
            </button>
          ) : (
            <span key={o.id} className={`fawkes-relatie-rol${on ? " is-on" : ""}`}>
              {inhoud}
            </span>
          );
        })}
      </div>
      <p className="fawkes-relatie-rollen__note">
        <Info size={15} aria-hidden="true" />
        {t("relatieDetailView.section.rolCategorieNote")}
      </p>

      {/* De tags staan in de bron in een eigen blok met een gestippelde scheiding erboven, zodat
          ze bij de rollen horen maar er niet mee versmelten. */}
      <div className="fawkes-relatie-tagblok">
        <h5 className="fawkes-relatie-tile__subhead">
          <Tag size={18} aria-hidden="true" />
          {t("relatieDetailView.section.tags")}
        </h5>
        <p className="fawkes-relatie-rollen__hint">{t("relatieDetailView.section.tagsHint")}</p>
        <div className="fawkes-relatie-chips fawkes-relatie-chips--tags">
          {tags}
          {onTagToevoegen && (
            <button type="button" className="fawkes-relatie-tag-add" onClick={onTagToevoegen}>
              <Plus size={14} aria-hidden="true" />
              {tagToevoegenLabel ?? t("relatieDetailView.section.tagToevoegen")}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// In-edit role/tag picker — toggle chips backed by the editor-only catalogs (GET /api/relaties/roles
// + /tags, gated ROLE_RELATIE_EDIT). Rendered only in edit mode so a non-editor never triggers the
// gated fetch. Selecting/deselecting toggles membership in the form's id arrays.
interface TagOptie {
  id: number;
  naam: string;
  kleur: string | null;
}

/**
 * De module die een rol afdekt, of null als de rol altijd kiesbaar is
 * (PersoonsgegevensTileFragment#rolModule).
 */
function rolModule(code: string): string | null {
  switch (code) {
    case "ASIEL":
    case "DIERENAMBULANCE":
      return "asiel";
    case "DONATEUR":
      return "donateur";
    case "PLEEGGEZIN":
      return "pleeggezin";
    case "LEVERANCIER":
      return "leverancier";
    // Een dierenartspraktijk hoort bij de opvang zelf: je hebt er een nodig voor de dieren die er
    // zitten, los van of het crematorium aanstaat.
    case "DIERENARTSPRAKTIJK":
      return "asiel";
    // Derde partij is een crematoriumbegrip; zonder die module valt er niets mee te doen.
    case "DERDE_PARTIJ":
      return "crematorium";
    default:
      return null;
  }
}

function RoleTagPicker({
  roleIds,
  tagIds,
  onToggleRole,
  onToggleTag,
}: {
  roleIds: number[];
  tagIds: number[];
  onToggleRole: (id: number) => void;
  onToggleTag: (id: number) => void;
}) {
  const t = useTranslate();
  const roles = useCustom({ url: "relaties/roles", method: "get" });
  const tags = useCustom({ url: "relaties/tags", method: "get" });
  const { isModuleEnabled } = useModuleAvailability();
  const alleRollen = (roles.data?.data as RolOptie[] | undefined) ?? [];
  const roleOpts = alleRollen.filter((o) => {
    const module = rolModule(o.code);
    // Een rol die de relatie al draagt blijft staan, ook als de module later uitging: anders zou
    // opslaan hem stilletjes weghalen.
    return module == null || isModuleEnabled(module) || roleIds.includes(o.id);
  });
  const tagOpts = (tags.data?.data as TagOptie[] | undefined) ?? [];
  const [kiezerOpen, setKiezerOpen] = useState(false);

  const gekozen = tagOpts.filter((tag) => tagIds.includes(tag.id));

  return (
    <>
      <RolCategorieSectie
        t={t}
        opties={roleOpts}
        geselecteerd={(o) => roleIds.includes(o.id)}
        onToggleRole={onToggleRole}
        onTagToevoegen={() => setKiezerOpen(true)}
        tagToevoegenLabel={t(
          gekozen.length === 0
            ? "relatieDetailView.section.tagToevoegen"
            : "relatieDetailView.section.tagToevoegenKort",
        )}
        tags={gekozen.map((tag) => (
          <span
            key={tag.id}
            // Witte letters horen alleen bij een chip die ook echt een eigen kleur onder zich heeft;
            // een tag zonder kleur werd zo wit op wit en was onzichtbaar.
            className={`fawkes-relatie-chip${tag.kleur ? " fawkes-relatie-chip--tag" : ""}`}
            style={tag.kleur ? { backgroundColor: tag.kleur, borderColor: tag.kleur } : undefined}
          >
            {tag.naam}
            <button
              type="button"
              className="fawkes-relatie-chip__weg"
              aria-label={t("common.delete")}
              title={t("common.delete")}
              onClick={() => onToggleTag(tag.id)}
            >
              ×
            </button>
          </span>
        ))}
      />
      {kiezerOpen && (
        <TagKiezerDialog
          opties={tagOpts}
          gekozenIds={tagIds}
          onToggle={onToggleTag}
          onClose={() => setKiezerOpen(false)}
          t={t}
        />
      )}
    </>
  );
}

/**
 * "Tags toevoegen" — de dialoog achter de knop in het tagblok. Eén aanvinklijst met alles wat er is,
 * voorgevinkt op wat de relatie al draagt. De kaart zelf toont daarna alleen nog wat gekozen is.
 */
function TagKiezerDialog({ opties, gekozenIds, onToggle, onClose, t }: {
  opties: TagOptie[];
  gekozenIds: number[];
  onToggle: (id: number) => void;
  onClose: () => void;
  t: (key: string) => string;
}) {
  // Een eigen kopie: pas op Opslaan gaat de keuze naar de kaart, zodat Annuleren ook echt
  // annuleert.
  const [keuze, setKeuze] = useState<number[]>(gekozenIds);

  const opslaan = () => {
    for (const tag of opties) {
      const had = gekozenIds.includes(tag.id);
      const wil = keuze.includes(tag.id);
      if (had !== wil) onToggle(tag.id);
    }
    onClose();
  };

  return (
    <Modal
      open
      title={t("relatieDetailView.section.tagsDialoog")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={opslaan}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-relatie-tagkiezer">
        {opties.length === 0 ? (
          <p className="fawkes-muted">{t("relatieDetailView.section.tagsGeen")}</p>
        ) : (
          opties.map((tag) => (
            <ToggleField
              key={tag.id}
              label={tag.naam}
              checked={keuze.includes(tag.id)}
              onChange={(e) =>
                setKeuze((vorig) =>
                  e.target.checked ? [...vorig, tag.id] : vorig.filter((id) => id !== tag.id),
                )
              }
            />
          ))
        )}
      </div>
    </Modal>
  );
}

/**
 * The read view of the same pair. An editor sees the whole catalog with this relation's roles ticked
 * (the Jmix grid); anyone else sees only the roles that apply — the catalog feed is editor-gated, so
 * asking for it would just 403. Either way the cards are static here; the pencil opens the picker.
 */
function RolTagChips({
  d,
  t,
  onBewerken,
}: {
  d: RelatieDetail;
  t: (k: string) => string;
  onBewerken?: () => void;
}) {
  const { data: permissions } = usePermissions<string[]>();
  const { isModuleEnabled } = useModuleAvailability();
  const canEdit = Array.isArray(permissions) && permissions.includes("ROLE_RELATIE_EDIT");
  const catalog = useCustom({ url: "relaties/roles", method: "get", queryOptions: { enabled: canEdit } });
  const catalogOpts = (catalog.data?.data as RolOptie[] | undefined) ?? [];

  // Without the catalog, the relation's own roles still render as cards — same shape, fewer of them.
  const eigen = new Set(d.roles.map((r) => r.code));
  const opties: RolOptie[] = (
    catalogOpts.length
      ? catalogOpts
      : d.roles.map((r, i) => ({ id: -1 - i, code: r.code, label: r.label, icoon: null }))
  ).filter((o) => {
    const module = rolModule(o.code);
    return module == null || isModuleEnabled(module) || eigen.has(o.code);
  });
  const eigenCodes = new Set(d.roles.map((r) => r.code));

  return (
    <RolCategorieSectie
      t={t}
      opties={opties}
      geselecteerd={(o) => eigenCodes.has(o.code)}
      onTagToevoegen={onBewerken}
      tags={d.tags.map((tag, i) => (
        <span
          key={`${tag.naam}-${i}`}
          className={`fawkes-relatie-chip${tag.kleur ? " fawkes-relatie-chip--tag" : ""}`}
          style={tag.kleur ? { backgroundColor: tag.kleur, borderColor: tag.kleur } : undefined}
        >
          {tag.naam}
        </span>
      ))}
    />
  );
}

/**
 * Which parts of the relation card the current user sees — the Jmix "Velden tonen of verbergen"
 * dialog, on the shared user_relatie_field_preference table, so a user who set this in Jmix sees the
 * same here. A missing or failing feed simply shows everything, the Jmix default.
 */
export function useRelatieVeldVoorkeuren() {
  const { data, refetch } = useCustom<{ fieldKey: string; visible: boolean }[]>({
    url: "relaties/field-preferences",
    method: "get",
  });
  // Guard the shape: a feed that is missing, erroring or mocked to something else must not take the
  // whole card down — everything visible is the Jmix default anyway.
  const raw = data?.data as unknown;
  const rijen = Array.isArray(raw) ? (raw as { fieldKey: string; visible: boolean }[]) : [];
  const zichtbaar = (key: string) => rijen.find((r) => r.fieldKey === key)?.visible ?? true;
  return { rijen, zichtbaar, refetch };
}

/** The relation card's own parts — the keys the Jmix "Velden tonen of verbergen" dialog lists. */
const RELATIE_VELD_KEYS = ["AANHEF", "TUSSENVOEGSEL", "ADRES", "CONTACT", "ROL_CATEGORIE"];
/** The four figures at the head of the Facturen tile — the Jmix "Facturen totalen" dialog. */
const FACTUUR_TOTAAL_KEYS = [
  "FACTUREN_OPENSTAAND",
  "FACTUREN_BETAALD",
  "FACTUREN_VERVALLEN",
  "FACTUREN_TOTAAL",
];

/**
 * The Jmix dialog behind a tile's settings icon: one switch per entry, with Annuleren / Opslaan.
 * Edits are staged and only written on Opslaan, exactly as there. Both Jmix dialogs write the same
 * preference table, so this one is shared and only the key set differs.
 */
function VoorkeurDialog({
  open,
  onClose,
  titel,
  keys,
}: {
  open: boolean;
  onClose: () => void;
  titel: string;
  keys: string[];
}) {
  const t = useTranslate();
  const { rijen, refetch } = useRelatieVeldVoorkeuren();
  const { mutate, isLoading: saving } = useCustomMutation();
  const [concept, setConcept] = useState<Record<string, boolean>>({});
  // Keep the dialog's own order (the Jmix one), and fall back to visible for a key the feed has not
  // stored yet — that is what the server answers for an untouched preference anyway.
  const getoond = keys.map((key) => ({
    fieldKey: key,
    visible: rijen.find((r) => r.fieldKey === key)?.visible ?? true,
  }));

  // Re-seed each time the dialog opens so a cancelled edit never lingers.
  useEffect(() => {
    if (open) {
      setConcept(Object.fromEntries(getoond.map((r) => [r.fieldKey, r.visible])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rijen]);

  const opslaan = () => {
    mutate(
      {
        url: "relaties/field-preferences",
        method: "put",
        values: { preferences: Object.entries(concept).map(([fieldKey, visible]) => ({ fieldKey, visible })) },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => { refetch(); onClose(); } },
    );
  };

  return (
    <Modal
      open={open}
      title={titel}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={opslaan} disabled={saving}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-pleeg-cap__categorieen fawkes-relatie-velden">
        {getoond.map((r) => (
          <button
            key={r.fieldKey}
            type="button"
            role="switch"
            aria-checked={concept[r.fieldKey] ?? r.visible}
            className={`fawkes-pleeg-cap__cat${(concept[r.fieldKey] ?? r.visible) ? " is-on" : ""}`}
            onClick={() => setConcept((c) => ({ ...c, [r.fieldKey]: !(c[r.fieldKey] ?? r.visible) }))}
          >
            <span className="fawkes-pleeg-cap__knop" aria-hidden="true" />
            {t(`relatieDetailView.velden.${r.fieldKey}`)}
          </button>
        ))}
      </div>
    </Modal>
  );
}

export function RelatieVeldenDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useTranslate();
  return (
    <VoorkeurDialog open={open} onClose={onClose} titel={t("relatieDetailView.velden.titel")} keys={RELATIE_VELD_KEYS} />
  );
}

export function FacturenTotalenDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useTranslate();
  return (
    <VoorkeurDialog
      open={open}
      onClose={onClose}
      titel={t("relatieDetailView.facturen.totalenTitel")}
      keys={FACTUUR_TOTAAL_KEYS}
    />
  );
}

/**
 * "Bestaande relaties op dit adres" — the Jmix warning: once postcode and house number are both
 * filled, other relations at that address are looked up, and each can be opened straight from the
 * dialog. It stays silent while the address is being typed (only complete pairs are asked for) and
 * says nothing at all when the address is this relation's alone.
 */
function RelatiesOpAdres({
  relatieId,
  postcode,
  huisnummer,
  toevoeging,
}: {
  relatieId: string;
  postcode: string;
  huisnummer: string;
  toevoeging: string;
}) {
  const t = useTranslate();
  const compleet = postcode.trim() !== "" && huisnummer.trim() !== "";
  const { data } = useCustom<RelatieOpAdres[]>({
    url: "relaties/op-adres",
    method: "get",
    config: {
      query: {
        postcode: postcode.trim(),
        huisnummer: huisnummer.trim(),
        toevoeging: toevoeging.trim(),
        exclude: relatieId,
      },
    },
    queryOptions: { enabled: compleet },
  });
  const rijen = Array.isArray(data?.data) ? data.data : [];
  const [open, setOpen] = useState(false);

  if (!compleet || rijen.length === 0) return null;
  return (
    <>
      <p className="fawkes-relatie-hint">
        <Info size={15} aria-hidden="true" />
        {t("relatieDetailView.adres.bestaandeRelaties", { args: [rijen.length] })}
        <button type="button" className="fawkes-relatie-alleslink" onClick={() => setOpen(true)}>
          {t("relatieDetailView.adres.bekijken")}
        </button>
      </p>
      <Modal
        open={open}
        title={t("relatieDetailView.adres.dialoogTitel")}
        onClose={() => setOpen(false)}
        footer={<Button variant="secondary" onClick={() => setOpen(false)}>{t("common.close")}</Button>}
      >
        <ul className="fawkes-relatie-plaats__lijst">
          {rijen.map((r) => (
            <li key={r.id} className="fawkes-relatie-plaats__kaart">
              <div className="fawkes-relatie-verblijf__body">
                <span className="fawkes-relatie-verblijf__title"><strong>{r.klantnaam ?? "—"}</strong></span>
                <span className="fawkes-relatie-media__sub">
                  {[r.klantNummer == null ? null : `#${r.klantNummer}`, r.woonplaats].filter(Boolean).join(" · ")}
                </span>
              </div>
              <Button variant="secondary" onClick={() => { window.location.hash = ""; window.location.assign(`/relatie/${r.id}`); }}>
                {t("relatieDetailView.adres.openen")}
              </Button>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}

export function PersoonsgegevensTile({ detail }: { detail: RelatieDetail }) {
  const t = useTranslate();
  const d = detail;
  const editable = true; // person + organisation both editable (write phase)

  const { data: permissions } = usePermissions<string[]>();
  const canEditRelatie = Array.isArray(permissions) && permissions.includes("ROLE_RELATIE_EDIT");
  const { zichtbaar } = useRelatieVeldVoorkeuren();
  const [form, setForm] = useState<PersoonForm>(() => toPersoonForm(d));
  const [notice, setNotice] = useState<PersoonNotice>(null);
  const { mutate, isLoading: saving } = useUpdate();

  // Re-seed when the detail changes (after a save bumps version, or navigation to another relation).
  useEffect(() => {
    setForm(toPersoonForm(d));
    setNotice(null);
  }, [d.id, d.version]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Flip person ↔ organisation, carrying the values across the way Jmix does
   * (copyPersoonToOrganisatie / copyOrganisatieToPersoon with copyTextIfEmpty): the other side is only
   * filled where it is still empty, so switching back and forth never overwrites what is already there
   * and never throws the old name away.
   */
  const wisselType = (organisatie: boolean) => {
    setNotice(null);
    setForm((f) => {
      if (f.organisatie === organisatie) return f;
      const leeg = (v: string) => v.trim() === "";
      return organisatie
        ? { ...f, organisatie, bedrijfsnaam: leeg(f.bedrijfsnaam) ? f.achternaam : f.bedrijfsnaam }
        : { ...f, organisatie, achternaam: leeg(f.achternaam) ? f.bedrijfsnaam : f.achternaam };
    });
  };

  const set = (k: keyof PersoonForm, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setNotice(null);
  };

  const toggleId = (key: "roleIds" | "tagIds", id: number) => {
    setForm((f) => {
      const has = f[key].includes(id);
      return { ...f, [key]: has ? f[key].filter((x) => x !== id) : [...f[key], id] };
    });
    setNotice(null);
  };

  const save = () => {
    if (form.organisatie && !form.bedrijfsnaam.trim()) {
      setNotice("requiredBedrijf");
      return;
    }
    if (!form.organisatie && !form.achternaam.trim()) {
      setNotice("required");
      return;
    }
    mutate(
      {
        resource: "relaties",
        id: d.id,
        values: { ...form, version: d.version },
      },
      {
        onSuccess: () => {
          setNotice("saved");
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          setNotice(
            code === 409 ? "conflict"
              : code === 403 ? "forbidden"
                : code === 422 ? "pleeggezinInUse"
                  : "error",
          );
        },
      },
    );
  };

  const noticeText =
    notice === "saved"
      ? t("relatieDetailView.edit.saved")
      : notice === "conflict"
        ? t("relatieDetailView.edit.conflict")
        : notice === "forbidden"
          ? t("relatieDetailView.edit.forbidden")
          : notice === "required"
            ? t("relatieDetailView.edit.required")
            : notice === "requiredBedrijf"
              ? t("relatieDetailView.edit.requiredBedrijf")
              : notice === "pleeggezinInUse"
                ? t("relatieDetailView.edit.pleeggezinInUse")
                : notice === "error"
                  ? t("relatieDetailView.edit.error")
                  : null;

  // One always-editable card, the way Jmix builds it: no pencil, no edit mode. Opslaan and Annuleren
  // are in the DOM the whole time but only surface once something actually changed (Jmix wires the same
  // dirty tracking and hides its footer until then).
  const schoon = toPersoonForm(d);
  const vuil = JSON.stringify(form) !== JSON.stringify(schoon);

  return (
    <div className="fawkes-relatie-tile">
      {/* The pickers behind the salutation and country fields. Both are datalists: the stored data
          holds values these lists do not (Mvr., Hr., a stray initial), and a strict select would
          silently blank them on the next save. */}
      <section className="fawkes-relatie-subcard">
        <h5 className="fawkes-relatie-tile__subhead fawkes-relatie-tile__subhead--card">
          <User size={18} aria-hidden="true" />
          {t(form.organisatie ? "relatieDetailView.section.bedrijf" : "relatieDetailView.section.persoon")}
        </h5>
        {/* Het label en het schuifje delen een regel: het label is kort en het schuifje heeft de
            breedte niet nodig, dus samen schelen ze een hele regel bovenin de kaart. */}
        <div className="fawkes-relatie-tile__typerij">
        <span className="fawkes-relatie-tile__keuzelabel">
          <Users size={17} aria-hidden="true" />
          {t("relatieDetailView.field.type")}
        </span>
        {/* Switching type carries the values over instead of stranding them — Jmix copies person ↔
            organisation and only fills fields that are still empty, so nothing typed is overwritten. */}
        <div className="fawkes-relatie-tile__type" role="group" aria-label={t("relatieDetailView.field.type")}>
          {[false, true].map((org) => (
            <button
              key={String(org)}
              type="button"
              aria-pressed={form.organisatie === org}
              className={"fawkes-relatie-tile__type-option" + (form.organisatie === org ? " is-active" : "")}
              disabled={!editable}
              onClick={() => wisselType(org)}
            >
              {t(org ? "relatieDetailView.type.organisatie" : "relatieDetailView.type.persoon")}
            </button>
          ))}
        </div>
        </div>
        <h5 className="fawkes-relatie-tile__subhead">
          {form.organisatie ? <Building2 size={18} aria-hidden="true" /> : <User size={18} aria-hidden="true" />}
          {t(form.organisatie ? "relatieDetailView.section.bedrijf" : "relatieDetailView.section.persoon")}
        </h5>
        {form.organisatie ? (
          <>
          {/* De bedrijfsnaam over de volle breedte, KvK en BTW daaronder samen op een regel — de
              indeling van het fragment. Ze deelden hier het raster met de naamvelden, en die zijn juist
              smal: een KvK-nummer kreeg zo de breedte van een aanhef. */}
          <div className="fawkes-relatie-rij">
            <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
              <Field label={t("relatieDetailView.field.bedrijfsnaam")} value={form.bedrijfsnaam} onChange={(e) => set("bedrijfsnaam", e.target.value)} required />
            </div>
          </div>
          <div className="fawkes-relatie-rij">
            <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
              <Field label={t("relatieDetailView.field.kvk")} value={form.kvkNummer} onChange={(e) => set("kvkNummer", e.target.value)} />
            </div>
            <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
              <Field label={t("relatieDetailView.field.btw")} value={form.btwNummer} onChange={(e) => set("btwNummer", e.target.value)} />
            </div>
          </div>
          </>
        ) : (
          <>
          <div className="fawkes-relatie-rij">
            {zichtbaar("AANHEF") && (
              // De bron kiest de aanhef uit een lijst; vrij intypen is hoe dezelfde persoon in de
              // gegevens als "Mevr.", "Mevr" en "Mvr." terechtkomt.
              <div className="fawkes-relatie-veld fawkes-relatie-veld--aanhef">
                <SelectField
                  label={t("relatieDetailView.field.aanhef")}
                  value={form.aanhef}
                  onChange={(e) => set("aanhef", e.target.value)}
                  options={aanhefOpties(form.aanhef)}
                />
              </div>
            )}
            <div className="fawkes-relatie-veld fawkes-relatie-veld--voorletters">
              <Field label={t("relatieDetailView.field.voorletters")} value={form.voorletters} onChange={(e) => set("voorletters", e.target.value)} />
            </div>
            {zichtbaar("TUSSENVOEGSEL") && (
              <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
                <Field label={t("relatieDetailView.field.tussenvoegsel")} value={form.tussenvoegsel} onChange={(e) => set("tussenvoegsel", e.target.value)} />
              </div>
            )}
          </div>
          <div className="fawkes-relatie-rij">
            {/* Jmix's card carries aanhef / voorletters / tussenvoegsel / achternaam and nothing else:
                a person's given name lives in the initials there. Voornaam and geboortedatum are only
                shown when the record actually has them — the update endpoint carries neither, so they
                stay read-only, and an empty one no longer takes up a column for a dash. */}
            <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
              <Field label={t("relatieDetailView.field.achternaam")} value={form.achternaam} onChange={(e) => set("achternaam", e.target.value)} required />
            </div>

          </div>
          </>
        )}
      </section>

      {zichtbaar("ADRES") && (
      <>
      <h5 className="fawkes-relatie-tile__subhead"><House size={18} aria-hidden="true" />{t("relatieDetailView.section.adres")}</h5>
      <div className="fawkes-relatie-rij">
        <div className="fawkes-relatie-veld fawkes-relatie-veld--straat">
          <Field label={t("relatieDetailView.field.straat")} value={form.straat} onChange={(e) => set("straat", e.target.value)} />
        </div>
        <div className="fawkes-relatie-veld fawkes-relatie-veld--huisnr">
          <Field label={t("relatieDetailView.field.huisnummer")} value={form.huisnummer} onChange={(e) => set("huisnummer", e.target.value)} />
        </div>
        <div className="fawkes-relatie-veld fawkes-relatie-veld--toevoeging">
          <Field label={t("relatieDetailView.field.toevoeging")} value={form.huisnummerToevoeging} onChange={(e) => set("huisnummerToevoeging", e.target.value)} />
        </div>
      </div>
      <div className="fawkes-relatie-rij">
        <div className="fawkes-relatie-veld fawkes-relatie-veld--postcode">
          <Field label={t("relatieDetailView.field.postcode")} value={form.postcode} onChange={(e) => set("postcode", e.target.value)} />
        </div>
        <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
          <Field label={t("relatieDetailView.field.woonplaats")} value={form.woonplaats} onChange={(e) => set("woonplaats", e.target.value)} />
        </div>
      </div>
      <div className="fawkes-relatie-rij">
        {/* De bron kiest het land uit een lijst en bewaart de code. Een tekstveld met suggesties liet
            ook een zelfverzonnen waarde door, en dan staat er straks "Nederland" waar "NL" hoort. */}
        <div className="fawkes-relatie-veld fawkes-relatie-veld--breed fawkes-relatie-veld--land">
          <Landvlag code={form.land} />
          <SelectField
            label={t("relatieDetailView.field.land")}
            value={form.land}
            onChange={(e) => set("land", e.target.value)}
            options={landOpties(form.land)}
          />
        </div>
      </div>

      {/* Jmix looks the address up as soon as postcode + huisnummer are filled and warns when someone
          already lives there — the guard against a second record for the same household. */}
      <RelatiesOpAdres
        relatieId={String(d.id)}
        postcode={form.postcode}
        huisnummer={form.huisnummer}
        toevoeging={form.huisnummerToevoeging}
      />

      </>
      )}

      {form.organisatie && (
      <>
      <h5 className="fawkes-relatie-tile__subhead">
        <User size={18} aria-hidden="true" />
        {t("relatieDetailView.section.contactpersoon")}
      </h5>
      <div className="fawkes-relatie-rij">
        {zichtbaar("AANHEF") && (
          <div className="fawkes-relatie-veld fawkes-relatie-veld--aanhef">
            <SelectField
              label={t("relatieDetailView.field.aanhef")}
              value={form.aanhef}
              onChange={(e) => set("aanhef", e.target.value)}
              options={aanhefOpties(form.aanhef)}
            />
          </div>
        )}
        <div className="fawkes-relatie-veld fawkes-relatie-veld--voorletters">
          <Field label={t("relatieDetailView.field.voorletters")} value={form.voorletters} onChange={(e) => set("voorletters", e.target.value)} />
        </div>
        {zichtbaar("TUSSENVOEGSEL") && (
          <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
            <Field label={t("relatieDetailView.field.tussenvoegsel")} value={form.tussenvoegsel} onChange={(e) => set("tussenvoegsel", e.target.value)} />
          </div>
        )}
      </div>
      <div className="fawkes-relatie-rij">
        <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
          <Field label={t("relatieDetailView.field.achternaam")} value={form.achternaam} onChange={(e) => set("achternaam", e.target.value)} />
        </div>
      </div>
      </>
      )}

      {zichtbaar("CONTACT") && (
      <>
      <h5 className="fawkes-relatie-tile__subhead"><Mail size={18} aria-hidden="true" />{t("relatieDetailView.section.contact")}</h5>
      <div className="fawkes-relatie-rij">
        <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
          <Field label={t("relatieDetailView.field.email")} type="email" invalidEmailMessage={t("validatie.emailadres")}
            value={form.email} onChange={(e) => set("email", e.target.value)} />
        </div>
      </div>
      <div className="fawkes-relatie-rij">
        <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
          <Field label={t("relatieDetailView.field.telefoon1")} value={form.telefoon1} onChange={(e) => set("telefoon1", e.target.value)} />
        </div>
        <div className="fawkes-relatie-veld fawkes-relatie-veld--breed">
          <Field label={t("relatieDetailView.field.telefoon2")} value={form.telefoon2} onChange={(e) => set("telefoon2", e.target.value)} />
        </div>
      </div>

      </>
      )}

      {/* Jmix puts the blacklist action at the foot of the card, under the contact details — at the
          top it was the first thing you saw on every relation. */}
      <BlokkadeBanner relatieId={String(d.id)} />

      {!zichtbaar("ROL_CATEGORIE") ? null : canEditRelatie ? (
        <RoleTagPicker
          roleIds={form.roleIds}
          tagIds={form.tagIds}
          onToggleRole={(id) => toggleId("roleIds", id)}
          onToggleTag={(id) => toggleId("tagIds", id)}
        />
      ) : (
        <RolTagChips d={d} t={t} />
      )}

      {(vuil || noticeText) && (
        <div className="fawkes-relatie-tile__actions">
          {noticeText && <span className={`fawkes-relatie-tile__notice fawkes-relatie-tile__notice--${notice}`} role="status">{noticeText}</span>}
          {vuil && (
            <>
              <Button variant="secondary" onClick={() => { setForm(toPersoonForm(d)); setNotice(null); }} disabled={saving}>
                {t("relatieDetailView.edit.cancel")}
              </Button>
              <Button variant="primary" onClick={save} disabled={saving}>
                {t("relatieDetailView.edit.save")}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Facturen --------------------------------------------------------------------------------------
// Read-only port of the Jmix FacturenTileFragment: four summary figures (outstanding / paid / overdue
// amounts + invoice count) over a per-invoice table with a derived status badge. The api serves the
// rows newest-first with the status + totals already derived (FacturenService parity).
// OPENSTAAND = warning (orange), matching Jmix FactuurFormat.statusModifier.
const FACTUUR_STATUS_TONE: Record<FactuurRegel["status"], StatusTone> = {
  CONCEPT: "neutral",
  BETAALD: "success",
  OPENSTAAND: "warning",
  VERVALLEN: "danger",
};

/**
 * The "onderdelen" the tile filters on. The api derives each invoice's module from the per-type
 * wrapper pointing at it (asiel_factuur / pension_reservering_factuur / creche_factuur / shop); these
 * four are the buttons Jmix always shows, and any other module present in the rows is appended so no
 * invoice hides behind a filter that isn't there.
 */
const FACTUUR_MODULES = ["ALLE", "ASIEL", "PENSION", "LOSSE_VERKOOP"];

/**
 * How many rows fit in the space the board row actually gave this tile — "fill it up until no more
 * fit, then the button".
 *
 * The container is a flex child with basis 0 and hidden overflow, so its height is decided by the tile
 * (which a taller neighbour stretches) and never by the rows inside it. That breaks the circle: adding
 * a row cannot make the container taller, so the measurement settles instead of feeding back.
 *
 * The row pitch is measured from what is on screen — total content height over rows rendered — rather
 * than assumed, so it stays right through a zoom, a density change or a wrapping row. `terugval` only
 * covers the very first paint, before there is anything to measure.
 */
function usePassendAantal<T extends HTMLElement>(terugval: number, minimum: number) {
  const [aantal, setAantal] = useState(minimum);
  const observerRef = useRef<ResizeObserver | null>(null);
  const meetRef = useRef<(() => void) | null>(null);

  // A CALLBACK ref, not useRef + useEffect. The tile renders a loading state first, so on the mount
  // effect the list element does not exist yet — the observer was never attached and the count stayed
  // at its minimum forever (4 invoice rows in a 1082px box). A callback ref fires when the node
  // actually appears, and again if it is swapped out.
  const ref = useCallback(
    (el: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!el || typeof ResizeObserver === "undefined") {
        return;
      }
      const meet = () => {
        // A table header eats from the same space but is not a row.
        const kop = el.querySelector("thead");
        const kopHoogte = kop instanceof HTMLElement ? kop.offsetHeight : 0;
        const ruimte = el.clientHeight - kopHoogte;
        const rijen = [...el.querySelectorAll("tbody tr, li")].filter(
          (r): r is HTMLElement => r instanceof HTMLElement,
        );
        const stel = (n: number) =>
          setAantal((vorig) => {
            const volgend = Math.max(minimum, n);
            return volgend === vorig ? vorig : volgend;
          });
        if (rijen.length === 0) {
          stel(Math.floor(ruimte / terugval));
          return;
        }
        // Count the rows that actually fit rather than dividing by an assumed row height: a stay card
        // with a two-line address is taller than its neighbour, and a fixed pitch left the last one
        // sliced in half at the bottom edge. Rows are measured against the first row's top, so this
        // works for both a table body and a list.
        const top = rijen[0].offsetTop;
        const onder = (r: HTMLElement) => r.offsetTop - top + r.offsetHeight;
        const passend = rijen.filter((r) => onder(r) <= ruimte).length;
        const laatste = rijen[rijen.length - 1];
        // NOT scrollHeight/rows: scrollHeight never drops below clientHeight, so on a container taller
        // than its content it reports the container back and the count locks at the minimum.
        const pitch =
          rijen.length >= 2
            ? Math.max(1, (laatste.offsetTop - top) / (rijen.length - 1))
            : Math.max(1, laatste.offsetHeight);
        // Every rendered row fits and there is room left → ask for more; the next pass re-measures.
        const extra = passend === rijen.length ? Math.floor((ruimte - onder(laatste)) / pitch) : 0;
        stel(passend + extra);
      };
      meetRef.current = meet;
      meet();
      const ro = new ResizeObserver(meet);
      ro.observe(el);
      observerRef.current = ro;
    },
    [terugval, minimum],
  );

  // Growing the list changes its content, not its box, so the ResizeObserver stays silent — re-measure
  // after every render so the count converges on what really fits (grow, then trim any row that spills).
  useEffect(() => {
    meetRef.current?.();
  });

  return [ref, aantal] as const;
}
const FACTUUR_MODULE_ICONEN: Record<string, LucideIcon> = {
  ALLE: LayoutGrid,
  ASIEL: House,
  PENSION: BedDouble,
  LOSSE_VERKOOP: ShoppingCart,
  CRECHE: Sun,
  OVERIG: FileText,
};

export function FacturenTile({
  relatieId,
  onOpenFactuur,
}: {
  relatieId: string;
  onOpenFactuur?: (factuurId: string) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError } = useTile<FacturenTileData>(relatieId, "facturen");
  // Which of the four figures the user keeps — the Jmix "Facturen totalen" dialog, same table as the
  // relation card's fields.
  const { zichtbaar: toonTotaal } = useRelatieVeldVoorkeuren();
  const [module, setModule] = useState("ALLE");
  const [toonAlles, setToonAlles] = useState(false);
  // Minimum one row: the measurement runs from the ref callback, before paint, so the count is right
  // by the first frame — a higher floor only forced rows into a card too short to hold them.
  const [lijstRef, passendAantal] = usePassendAantal<HTMLDivElement>(44, 1);

  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;
  const d = data;
  // Jmix-parity: always show the four summary figures (zeros when empty); the table renders its own
  // empty message below. Previously an empty invoice list hid the totals entirely.
  if (!d || !d.regels) {
    return <p className="fawkes-tile-placeholder">{t("relatieDetailView.facturen.empty")}</p>;
  }

  const columns: Column<FactuurRegel>[] = [
    { header: t("relatieDetailView.facturen.col.nummer"), cell: (r) => r.nummer },
    { header: t("relatieDetailView.facturen.col.datum"), cell: (r) => formatDate(r.factuurDatum, locale) },
    { header: t("relatieDetailView.facturen.col.verval"), cell: (r) => formatDate(r.vervalDatum, locale) },
    {
      header: t("relatieDetailView.facturen.col.status"),
      cell: (r) => (
        <StatusBadge tone={FACTUUR_STATUS_TONE[r.status]}>
          {t(`relatieDetailView.facturen.status.${r.status}`)}
        </StatusBadge>
      ),
    },
    { header: t("relatieDetailView.facturen.col.bedrag"), cell: (r) => formatEuro(r.totaal, locale) },
    // Jmix ends each row with a chevron, so it reads as "this opens something".
    { header: "", cell: () => <ChevronRight size={16} aria-hidden="true" className="fawkes-relatie-row-chevron" /> },
  ];
  const meerFacturen = Math.max(0, d.aantalTotaal - d.regels.length);

  // The "onderdelen" buttons: the four Jmix ones always, plus any other module these invoices actually
  // came from, so a crèche or a hand-raised invoice is never silently unreachable behind the filter.
  const aanwezig = new Set(d.regels.map((r) => r.module ?? "OVERIG"));
  const modules: string[] = [
    "ALLE",
    ...FACTUUR_MODULES.filter((m) => m !== "ALLE"),
    ...[...aanwezig].filter((m) => !FACTUUR_MODULES.includes(m)).sort(),
  ];
  const zichtbaar = module === "ALLE" ? d.regels : d.regels.filter((r) => (r.module ?? "OVERIG") === module);
  const getoond = zichtbaar.slice(0, passendAantal);
  // The footer counts against the whole set the filter selects — including the rows the tile endpoint
  // capped away — so "Nog 35 meer · 50 totaal" adds up. A module filter narrows both halves.
  const totaalFacturen = module === "ALLE" ? d.aantalTotaal : zichtbaar.length;
  const restFacturen = Math.max(0, totaalFacturen - getoond.length);

  return (
    <div className="fawkes-relatie-tile fawkes-relatie-tile--fill">
      <div className="fawkes-relatie-facturen__totals">
        {toonTotaal("FACTUREN_OPENSTAAND") && <div className="fawkes-relatie-facturen__total">
          <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.facturen.totaalOpenstaand")}</span>
          <span className="fawkes-relatie-facturen__total-rij">
            <span className="fawkes-relatie-facturen__total-value">{formatEuro(d.totaalOpenstaand, locale)}</span>
            <span className="fawkes-relatie-facturen__total-count fawkes-relatie-facturen__total-count--openstaand">{t("relatieDetailView.facturen.countOpenstaand", { args: [d.aantalOpenstaand] })}</span>
          </span>
        </div>}
        {toonTotaal("FACTUREN_BETAALD") && <div className="fawkes-relatie-facturen__total">
          <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.facturen.totaalBetaald")}</span>
          <span className="fawkes-relatie-facturen__total-rij">
            <span className="fawkes-relatie-facturen__total-value">{formatEuro(d.totaalBetaald, locale)}</span>
            <span className="fawkes-relatie-facturen__total-count fawkes-relatie-facturen__total-count--betaald">{t("relatieDetailView.facturen.countBetaald", { args: [d.aantalBetaald] })}</span>
          </span>
        </div>}
        {toonTotaal("FACTUREN_VERVALLEN") && <div className="fawkes-relatie-facturen__total">
          <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.facturen.totaalVervallen")}</span>
          <span className="fawkes-relatie-facturen__total-rij">
            <span className="fawkes-relatie-facturen__total-value">{formatEuro(d.totaalVervallen, locale)}</span>
            <span className="fawkes-relatie-facturen__total-count fawkes-relatie-facturen__total-count--vervallen">{t("relatieDetailView.facturen.countVervallen", { args: [d.aantalVervallen] })}</span>
          </span>
        </div>}
        {toonTotaal("FACTUREN_TOTAAL") && <div className="fawkes-relatie-facturen__total fawkes-relatie-facturen__total--wide">
          <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.facturen.totaalFacturen")}</span>
          <span className="fawkes-relatie-facturen__total-value">{d.aantalTotaal}</span>
        </div>}
      </div>

      <div className="fawkes-relatie-onderdelen" role="group" aria-label={t("relatieDetailView.facturen.onderdelen")}>
        {modules.map((m) => {
          const Icon = FACTUUR_MODULE_ICONEN[m] ?? FileText;
          return (
            <button
              key={m}
              type="button"
              aria-pressed={module === m}
              className={`fawkes-relatie-onderdeel${module === m ? " is-on" : ""}`}
              onClick={() => setModule(m)}
            >
              <Icon size={16} aria-hidden="true" />
              {t(`relatieDetailView.facturen.module.${m}`)}
            </button>
          );
        })}
      </div>
      {/* A page of rows, then a button for the rest — an inner scrollbar inside a card that is itself
          on a scrolling page reads as a trap. "Rest bekijken" shows the remainder in place. */}
      <div className="fawkes-relatie-facturen__list" ref={lijstRef}>
        <IndeelbareTabel tabelKey="relatie-tiles-facturen"
          columns={columns}
          rows={getoond}
          getRowKey={(r) => String(r.id)}
          empty={t("relatieDetailView.facturen.empty")}
          onRowClick={onOpenFactuur ? (r) => onOpenFactuur(String(r.id)) : undefined}
        />
      </div>
      {/* Jmix closes the tile with a link on the left and the remainder on the right:
          "Bekijk alle facturen        Nog 35 meer · 50 totaal". */}
      <div className="fawkes-relatie-tegelvoet">
        <button type="button" className="fawkes-relatie-alleslink" onClick={() => setToonAlles(true)}>
          <FileText size={15} aria-hidden="true" />
          {t("relatieDetailView.facturen.bekijkAlle")}
        </button>
        {restFacturen > 0 && (
          <span className="fawkes-relatie-tegelvoet__rest">
            {t("relatieDetailView.nogMeer", { args: [restFacturen, totaalFacturen] })}
          </span>
        )}
      </div>
      {/* The tile shows what fits; the rest opens in a dialog rather than growing the card. */}
      <Modal
        open={toonAlles}
        title={t("relatieDetailView.facturen.alleTitel")}
        size="wide"
        onClose={() => setToonAlles(false)}
        footer={<Button variant="secondary" onClick={() => setToonAlles(false)}>{t("common.close")}</Button>}
      >
        <div className="fawkes-relatie-alles">
          <IndeelbareTabel tabelKey="relatie-tiles-facturen-2"
            columns={columns}
            rows={zichtbaar}
            getRowKey={(r) => String(r.id)}
            empty={t("relatieDetailView.facturen.empty")}
            onRowClick={onOpenFactuur ? (r) => { setToonAlles(false); onOpenFactuur(String(r.id)); } : undefined}
          />
        </div>
      </Modal>
      {/* Beyond the api's own cap there is nothing loaded to reveal — say so rather than let the list
          look complete. */}
      {restFacturen === 0 && meerFacturen > 0 && (
        <p className="fawkes-relatie-tile__more">
          {t("relatieDetailView.facturen.meer", { args: [meerFacturen, d.aantalTotaal] })}
        </p>
      )}
    </div>
  );
}

// ---- Verblijven ------------------------------------------------------------------------------------
// Read-only port of the Jmix VerblijvenTileFragment: stays where the relation was the bringer or
// receiver, with three summary figures (active / total / last departure) over a per-stay table. The
// animal photo thumbnail is deferred with the Media binary work — a diersoort emoji stands in for now.
// In Jmix this tile belongs to the asiel module; module-gating has no api signal yet, so the tile is
// shown unconditionally here (surface, don't fake) — a module-availability feed is a follow-up.
function diersoortEmoji(diersoort: string | null): string {
  const s = (diersoort ?? "").toLowerCase();
  if (s.includes("kat")) return "🐱";
  if (s.includes("hond")) return "🐶";
  if (s.includes("konijn")) return "🐰";
  if (s.includes("vogel")) return "🐦";
  return "🐾";
}

export function VerblijvenTile({
  relatieId,
  onOpenDier,
}: {
  relatieId: string;
  onOpenDier?: (dierId: string) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError } = useTile<VerblijvenTileData>(relatieId, "verblijven");
  // Jmix filters this tile by branch and by period; both are answerable from the rows we already have.
  const [vestigingFilter, setVestigingFilter] = useState("");
  const [periodeFilter, setPeriodeFilter] = useState<"alles" | "actief" | "afgerond">("alles");
  const [toonAlles, setToonAlles] = useState(false);
  // Minimum one: a stay card is tall enough that two of them do not fit the tile's smallest height,
  // and forcing a second one only sliced it in half at the bottom edge.
  const [kaartenRef, passendAantal] = usePassendAantal<HTMLUListElement>(104, 1);
  // Photos for the stays on the tile, fetched authenticated as blobs (the bearer can't ride an <img
  // src>). Declared before the loading guards so the hook order stays stable across renders. The hook
  // caches and de-duplicates per animal, and the api already caps this feed, so the request count is
  // bounded by the stays this relation actually has.
  const fotoUrls = useAnimalPhotoUrls(
    (data?.regels ?? []).map((r) => r.dierId).filter((id): id is number => id != null),
  );

  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;
  const d = data;
  if (!d || !d.regels || d.regels.length === 0) {
    return <p className="fawkes-tile-placeholder">{t("relatieDetailView.verblijven.empty")}</p>;
  }

  const vestigingen = [...new Set(d.regels.map((r) => r.vestiging).filter((v): v is string => !!v))].sort();
  // What the api itself capped — nothing loaded is hidden behind that, so it stays a note, not a button.
  const meerVerblijven = Math.max(0, d.aantalTotaal - d.regels.length);
  const zichtbaar = d.regels.filter(
    (r) =>
      (!vestigingFilter || r.vestiging === vestigingFilter)
      && (periodeFilter === "alles"
        || (periodeFilter === "actief" ? r.status === "ACTIEF" : r.status !== "ACTIEF")),
  );
  const getoond = zichtbaar.slice(0, passendAantal);
  const restVerblijven = zichtbaar.length - getoond.length;


  return (
    <div className="fawkes-relatie-tile fawkes-relatie-tile--fill">
      <div className="fawkes-relatie-facturen__totals fawkes-relatie-verblijf__totalen">
        <div className="fawkes-relatie-facturen__total">
          <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.verblijven.actief")}</span>
          <span className="fawkes-relatie-verblijf__actief">
            <span className="fawkes-relatie-facturen__total-value">{d.aantalActief}</span>
            {d.aantalActief > 0 && <StatusBadge tone="success">{t("relatieDetailView.verblijven.status.ACTIEF")}</StatusBadge>}
          </span>
        </div>
        <div className="fawkes-relatie-facturen__total">
          <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.verblijven.totaal")}</span>
          <span className="fawkes-relatie-facturen__total-value">{d.aantalTotaal}</span>
        </div>
        <div className="fawkes-relatie-facturen__total">
          <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.verblijven.laatsteVertrek")}</span>
          <span className="fawkes-relatie-facturen__total-value">{d.laatsteVertrek ? formatDate(d.laatsteVertrek, locale) : "—"}</span>
        </div>
      </div>
      {/* Jmix lists stays as cards, not table rows: the animal with its species and role badges, the
          branch under it, then the period and how long it stayed. A seven-column table of the same
          facts is what made this tile read nothing like the Jmix screen. The two filters sit above them
          as pills carrying their own mark, not as labelled form fields. */}
      <div className="fawkes-relatie-verblijf__filters">
        <span className="fawkes-relatie-filterpil">
          <MapPin size={16} aria-hidden="true" />
          <select
            aria-label={t("relatieDetailView.verblijven.filter.vestiging")}
            value={vestigingFilter}
            onChange={(e) => setVestigingFilter(e.target.value)}
          >
            <option value="">{t("relatieDetailView.verblijven.filter.alleVestigingen")}</option>
            {vestigingen.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </span>
        <span className="fawkes-relatie-filterpil">
          <CalendarDays size={16} aria-hidden="true" />
          <select
            aria-label={t("relatieDetailView.verblijven.filter.periode")}
            value={periodeFilter}
            onChange={(e) => setPeriodeFilter(e.target.value as "alles" | "actief" | "afgerond")}
          >
            <option value="alles">{`${t("relatieDetailView.verblijven.filter.periode")}: ${t("relatieDetailView.verblijven.filter.alles")}`}</option>
            <option value="actief">{t("relatieDetailView.verblijven.filter.actief")}</option>
            <option value="afgerond">{t("relatieDetailView.verblijven.filter.afgerond")}</option>
          </select>
        </span>
      </div>
      <ul className="fawkes-relatie-verblijf__cards" ref={kaartenRef}>
        {getoond.map((r) => {
          const foto = r.dierId != null ? fotoUrls[String(r.dierId)] : undefined;
          return (
            <li
              key={r.id}
              className={`fawkes-relatie-verblijf__card${onOpenDier && r.dierId != null ? " is-klikbaar" : ""}`}
              role={onOpenDier && r.dierId != null ? "button" : undefined}
              tabIndex={onOpenDier && r.dierId != null ? 0 : undefined}
              onClick={onOpenDier && r.dierId != null ? () => onOpenDier(String(r.dierId)) : undefined}
              onKeyDown={
                onOpenDier && r.dierId != null
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpenDier(String(r.dierId));
                      }
                    }
                  : undefined
              }
            >
              {/* The animal's own photo, fetched authenticated as a blob; the species emoji is the
                  stand-in for an animal that has none. */}
              <span className="fawkes-relatie-verblijf__thumb">
                {foto ? <img src={foto} alt="" /> : <span aria-hidden="true">{diersoortEmoji(r.diersoort)}</span>}
              </span>
              <div className="fawkes-relatie-verblijf__body">
                <span className="fawkes-relatie-verblijf__title">
                  <span className="fawkes-relatie-verblijf__naam">{r.dierNaam ?? "—"}</span>
                  {r.diersoort && <StatusBadge tone="neutral">{r.diersoort}</StatusBadge>}
                  {/* Brenger and haler are opposite ends of the same stay, so they get their own
                      colours — in one badge tone the two read as the same thing at a glance. */}
                  {r.rol && (
                    <span className={`fawkes-relatie-rolbadge fawkes-relatie-rolbadge--${r.rol.toLowerCase()}`}>
                      {r.rol}
                    </span>
                  )}
                </span>
                <span className="fawkes-relatie-verblijf__line">{r.vestiging ?? "—"}</span>
                <span className="fawkes-relatie-verblijf__line">{r.opnameSoort ?? "—"}</span>
                <span className="fawkes-relatie-verblijf__streep" aria-hidden="true" />
                <span className="fawkes-relatie-verblijf__dates">
                  <CalendarDays size={14} aria-hidden="true" />
                  {formatDate(r.opnameDatum, locale)}
                  <span aria-hidden="true">→</span>
                  <CalendarDays size={14} aria-hidden="true" />
                  {r.vertrekDatum ? formatDate(r.vertrekDatum, locale) : t("relatieDetailView.verblijven.heden")}
                </span>
                <span className="fawkes-relatie-verblijf__duur">
                  {t(r.aantalDagen === 1 ? "relatieDetailView.verblijven.dag" : "relatieDetailView.verblijven.dagen", { args: [r.aantalDagen] })}
                </span>
              </div>
              <ChevronRight size={16} aria-hidden="true" className="fawkes-relatie-row-chevron" />
            </li>
          );
        })}
      </ul>
      {zichtbaar.length === 0 && (
        <p className="fawkes-tile-placeholder">{t("relatieDetailView.verblijven.filter.geen")}</p>
      )}
      <div className="fawkes-relatie-tegelvoet">
        <button type="button" className="fawkes-relatie-alleslink" onClick={() => setToonAlles(true)}>
          <House size={15} aria-hidden="true" />
          {t("relatieDetailView.verblijven.bekijkAlle")}
        </button>
        {(restVerblijven > 0 || meerVerblijven > 0) && (
          <span className="fawkes-relatie-tegelvoet__rest">
            {t("relatieDetailView.nogMeer", { args: [restVerblijven + meerVerblijven, d.aantalTotaal] })}
          </span>
        )}
      </div>

      {/* The tile shows what fits; the rest opens in a dialog rather than growing the card. */}
      <Modal
        open={toonAlles}
        title={t("relatieDetailView.verblijven.alleTitel")}
        size="wide"
        onClose={() => setToonAlles(false)}
        footer={<Button variant="secondary" onClick={() => setToonAlles(false)}>{t("common.close")}</Button>}
      >
        <ul className="fawkes-relatie-verblijf__cards fawkes-relatie-alles">
          {zichtbaar.map((r) => {
            const foto = r.dierId != null ? fotoUrls[String(r.dierId)] : undefined;
            const klikbaar = !!onOpenDier && r.dierId != null;
            return (
              <li
                key={r.id}
                className={`fawkes-relatie-verblijf__card${klikbaar ? " is-klikbaar" : ""}`}
                role={klikbaar ? "button" : undefined}
                tabIndex={klikbaar ? 0 : undefined}
                onClick={klikbaar ? () => { setToonAlles(false); onOpenDier!(String(r.dierId)); } : undefined}
              >
                <span className="fawkes-relatie-verblijf__thumb">
                  {foto ? <img src={foto} alt="" /> : <span aria-hidden="true">{diersoortEmoji(r.diersoort)}</span>}
                </span>
                <div className="fawkes-relatie-verblijf__body">
                  <span className="fawkes-relatie-verblijf__title">
                    <strong>{r.dierNaam ?? "—"}</strong>
                    {r.diersoort && <StatusBadge tone="neutral">{r.diersoort}</StatusBadge>}
                    {r.rol && (
                      <span className={`fawkes-relatie-rolbadge fawkes-relatie-rolbadge--${r.rol.toLowerCase()}`}>
                        {r.rol}
                      </span>
                    )}
                  </span>
                  <span className="fawkes-relatie-verblijf__line">{r.vestiging ?? "—"}</span>
                  <span className="fawkes-relatie-verblijf__line">{r.opnameSoort ?? "—"}</span>
                  <span className="fawkes-relatie-verblijf__dates">
                    <CalendarDays size={14} aria-hidden="true" />
                    {formatDate(r.opnameDatum, locale)}
                    <span aria-hidden="true">→</span>
                    <CalendarDays size={14} aria-hidden="true" />
                    {r.vertrekDatum ? formatDate(r.vertrekDatum, locale) : t("relatieDetailView.verblijven.heden")}
                  </span>
                  <span className="fawkes-relatie-verblijf__duur">
                    {t(r.aantalDagen === 1 ? "relatieDetailView.verblijven.dag" : "relatieDetailView.verblijven.dagen", { args: [r.aantalDagen] })}
                  </span>
                </div>
                <ChevronRight size={16} aria-hidden="true" className="fawkes-relatie-row-chevron" />
              </li>
            );
          })}
        </ul>
      </Modal>
    </div>
  );
}

// ---- Pleeggezin (foster) tab -----------------------------------------------------------------------
// Read-only port of the Jmix Pleeggezin panels: profiel (login + capacity + welcomed categories),
// current placements, historic placements and the platform-fed absences. Shown when the relation has
// the PLEEGGEZIN role; the Jmix module-gating has no api signal yet (surface, don't fake).

function PleeggezinProfielPanel({ relatieId }: { relatieId: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError, refetch } = useTile<PleeggezinProfielData>(relatieId, "pleeggezin");
  const { mutate, isLoading: saving } = useCustomMutation();
  // No edit mode: the Jmix panel is a live card — the switch flips straight away and the fields are
  // fields. Opslaan/Annuleren only surface once something differs from what is stored.
  const [form, setForm] = useState<{ loginActief: boolean; loginEmail: string; interneOpmerking: string } | null>(null);
  const [notice, setNotice] = useState<null | "saved" | "error" | "emailRequired" | "invited">(null);

  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;
  const d = data;
  if (!d) {
    return <p className="fawkes-tile-placeholder">{t("relatieDetailView.pleeggezin.geenProfiel")}</p>;
  }
  // The login profile is editable/creatable only once the relation has a platform uuid (the write
  // endpoint key). With a uuid but no profile row yet, the operator can CREATE one — the PUT upserts
  // (FosterProfileAdminService.save returns `created`), matching the Jmix panel which renders the
  // fillable form. Without a uuid there is nothing to write to, so the tab stays a read-only placeholder.
  const canEdit = !!d.klantUuid;
  const opgeslagen = {
    loginActief: d.loginActief,
    loginEmail: d.loginEmail ?? "",
    interneOpmerking: d.interneOpmerking ?? "",
  };
  const huidig = form ?? opgeslagen;
  const vuil = JSON.stringify(huidig) !== JSON.stringify(opgeslagen);
  const zet = (deel: Partial<typeof opgeslagen>) => {
    setNotice(null);
    setForm({ ...huidig, ...deel });
  };

  const save = () => {
    // An active login must carry an e-mail (the platform's activate contract); guard client-side too.
    if (huidig.loginActief && !huidig.loginEmail.trim()) {
      setNotice("emailRequired");
      return;
    }
    mutate(
      {
        url: `pleeggezinnen/${d.klantUuid}/profiel`,
        method: "put",
        values: {
          loginActief: huidig.loginActief,
          loginEmail: huidig.loginEmail.trim() || null,
          interneOpmerking: huidig.interneOpmerking.trim() || null,
        },
      },
      {
        onSuccess: () => {
          setForm(null);
          setNotice("saved");
          refetch();
        },
        onError: (err) =>
          setNotice((err as { statusCode?: number }).statusCode === 422 ? "emailRequired" : "error"),
      },
    );
  };

  // Re-send the portal invite / login link (passwordless) — re-enqueues the activate webhook.
  const sendInvite = () => {
    setNotice(null);
    mutate(
      { url: `pleeggezinnen/${d.klantUuid}/invite`, method: "post", values: {} },
      { onSuccess: () => setNotice("invited"), onError: () => setNotice("error") },
    );
  };
  const canInvite = canEdit && d.loginActief && !!(d.loginEmail && d.loginEmail.trim());

  return (
    <div className="fawkes-relatie-tile">
      {!d.heeftProfiel && !canEdit ? (
        <p className="fawkes-tile-placeholder">{t("relatieDetailView.pleeggezin.geenProfiel")}</p>
      ) : (
        <>
          <h5 className="fawkes-relatie-tile__subhead">
            <Users size={18} aria-hidden="true" />
            {t("relatieDetailView.pleeggezin.instellingenTitel")}
          </h5>
          <div className="fawkes-form-grid">
            <FieldShell label={t("relatieDetailView.pleeggezin.loginActief")} span2>
              {/* Jmix shows a slider here, the same one as the welcome categories below. */}
              <button
                type="button"
                role="switch"
                aria-checked={huidig.loginActief}
                disabled={!canEdit}
                className={`fawkes-pleeg-login${huidig.loginActief ? " is-on" : ""}`}
                onClick={() => zet({ loginActief: !huidig.loginActief })}
              >
                <span className="fawkes-pleeg-cap__knop" aria-hidden="true" />
                {t("relatieDetailView.pleeggezin.loginActiefUitleg")}
              </button>
            </FieldShell>
            <FieldShell label={t("relatieDetailView.pleeggezin.loginEmail")}>
              <input
                type="email"
                className="fui-field__input"
                value={huidig.loginEmail}
                disabled={!canEdit}
                onChange={(e) => zet({ loginEmail: e.target.value })}
              />
              {/* Jmix notes under this field where the address comes from. */}
              <span className="fawkes-relatie-veldhint">{t("relatieDetailView.pleeggezin.loginEmailHint")}</span>
            </FieldShell>
            <ReadField
              label={t("relatieDetailView.pleeggezin.laatsteLogin")}
              value={d.laatsteLogin ? formatDate(d.laatsteLogin, locale) : null}
            />
            <FieldShell label={t("relatieDetailView.pleeggezin.interneOpmerking")} span2>
              <textarea
                className="fui-field__input"
                rows={3}
                value={huidig.interneOpmerking}
                disabled={!canEdit}
                onChange={(e) => zet({ interneOpmerking: e.target.value })}
              />
            </FieldShell>
          </div>

          {/* The note Jmix puts under the switch, explaining what switching it on grants. */}
          <p className="fawkes-relatie-hint">
            <Info size={15} aria-hidden="true" />
            {t("relatieDetailView.pleeggezin.portaalUitleg")}
          </p>

          {notice === "emailRequired" && (
            <p className="fawkes-error">{t("relatieDetailView.pleeggezin.emailVerplicht")}</p>
          )}
          {notice === "error" && <p className="fawkes-error">{t("relatieDetailView.pleeggezin.opslaanMislukt")}</p>}
          {notice === "saved" && <p className="fawkes-success">{t("relatieDetailView.pleeggezin.opgeslagen")}</p>}
          {notice === "invited" && (
            <p className="fawkes-success">{t("relatieDetailView.pleeggezin.uitnodigingVerstuurd")}</p>
          )}

          {vuil && (
            <div className="fawkes-relatie-tile__actions">
              <Button variant="primary" onClick={save} disabled={saving}>{t("common.save")}</Button>
              <Button variant="secondary" onClick={() => { setForm(null); setNotice(null); }} disabled={saving}>
                {t("common.cancel")}
              </Button>
            </div>
          )}

          {/* Jmix closes the card with these two, side by side. */}
          {canInvite && (
            <div className="fawkes-pleeg-loginacties">
              <button type="button" className="fawkes-relatie-alleslink" onClick={sendInvite} disabled={saving}>
                <Mail size={15} aria-hidden="true" />
                {t("relatieDetailView.pleeggezin.stuurUitnodiging")}
              </button>
              <button type="button" className="fawkes-relatie-alleslink" onClick={sendInvite} disabled={saving}>
                <Shield size={15} aria-hidden="true" />
                {t("relatieDetailView.pleeggezin.resetInloglink")}
              </button>
            </div>
          )}
        </>
      )}

      <PleeggezinCapaciteitSection
        relatieId={relatieId}
        capaciteit={d.capaciteit ?? []}
        canEdit={canEdit}
        onChanged={refetch}
      />
    </div>
  );
}

/**
 * Capaciteit & voorkeuren — read-only summary (per-species max/huidig/vrij + welcomed-category chips)
 * with an inline edit mode that loads the current-vestiging catalog and lets the user set the max per
 * pleeggezin-active diersoort and toggle welcomed opvangcategorieën, saving each change immediately
 * (parity with the Jmix panel's direct-save IntegerField + checkboxes).
 */
/**
 * "Capaciteit & voorkeuren per diersoort" — one card per species, the way Jmix shows it: the maximum
 * on a minus/plus stepper with how many are currently there and how many spots are left beside it, and
 * the welcome categories as toggles underneath. No edit mode: the catalog is loaded up front and every
 * change writes straight through, which is what the two endpoints behind it already did.
 */
function PleeggezinCapaciteitSection({
  relatieId,
  capaciteit,
  canEdit,
  onChanged,
}: {
  relatieId: string;
  /** May be absent on a relation without a foster profile yet — the section then only offers the catalog. */
  capaciteit: PleeggezinCapaciteit[] | undefined;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const t = useTranslate();
  const { mutate } = useCustomMutation();
  const catalog = useCustom<{ data: CapaciteitCatalog }>({
    url: `relaties/${relatieId}/pleeggezin/capaciteit-catalog`,
    method: "get",
  });
  const [rows, setRows] = useState<CatalogDiersoort[]>([]);
  useEffect(() => {
    const data = catalog.data?.data as CapaciteitCatalog | undefined;
    if (data) setRows(data.diersoorten);
  }, [catalog.data]);

  // huidig/vrij come from the profile summary, the editable max + categories from the catalog.
  const bezetting = new Map((capaciteit ?? []).map((c) => [c.diersoortId, c]));

  const zetMax = (diersoortId: number, maxCapaciteit: number) => {
    const veilig = Math.max(0, Math.min(999, maxCapaciteit));
    setRows((rs) => rs.map((d) => (d.diersoortId === diersoortId ? { ...d, maxCapaciteit: veilig } : d)));
    mutate(
      {
        url: `relaties/${relatieId}/pleeggezin/capaciteit`,
        method: "put",
        values: { diersoortId, maxCapaciteit: veilig },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onChanged() },
    );
  };
  const toggleWelkom = (diersoortId: number, opvangcategorieId: number, welkom: boolean) => {
    setRows((rs) => rs.map((d) => d.diersoortId !== diersoortId ? d : {
      ...d, categorieen: d.categorieen.map((c) => c.opvangcategorieId === opvangcategorieId ? { ...c, welkom } : c),
    }));
    mutate(
      {
        url: `relaties/${relatieId}/pleeggezin/welkom-categorie`,
        method: "put",
        values: { opvangcategorieId, welkom },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onChanged() },
    );
  };

  const vestigingId = (catalog.data?.data as CapaciteitCatalog | undefined)?.vestigingId ?? null;

  return (
    <>
      <h5 className="fawkes-relatie-tile__subhead">
        <PawPrint size={18} aria-hidden="true" />
        {t("relatieDetailView.pleeggezin.capaciteit")}
      </h5>
      <p className="fawkes-relatie-rollen__hint">{t("relatieDetailView.pleeggezin.cap.uitleg")}</p>

      {catalog.isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : vestigingId === null ? (
        <p className="fawkes-tile-placeholder">{t("relatieDetailView.pleeggezin.cap.geenVestiging")}</p>
      ) : rows.length === 0 ? (
        <p className="fawkes-tile-placeholder">{t("relatieDetailView.pleeggezin.cap.geenDiersoorten")}</p>
      ) : (
        <div className="fawkes-pleeg-cap">
          {rows.map((d) => {
            const b = bezetting.get(d.diersoortId);
            const huidig = b?.huidig ?? 0;
            const vrij = b?.vrij ?? Math.max(0, d.maxCapaciteit - huidig);
            return (
              <section key={d.diersoortId} className="fawkes-pleeg-cap__soort">
                <div className="fawkes-pleeg-cap__kop">
                  <span className="fawkes-pleeg-cap__naam">
                    <span className="fawkes-pleeg-cap__emoji" aria-hidden="true">{diersoortEmoji(d.naam)}</span>
                    {d.naam ?? "—"}
                  </span>
                  <div className="fawkes-pleeg-cap__stat">
                    <span className="fawkes-pleeg-cap__label">{t("relatieDetailView.pleeggezin.cap.max")}</span>
                    <span className="fawkes-pleeg-cap__stepper">
                      <button
                        type="button"
                        aria-label={t("relatieDetailView.pleeggezin.cap.min")}
                        disabled={!canEdit || d.maxCapaciteit <= 0}
                        onClick={() => zetMax(d.diersoortId, d.maxCapaciteit - 1)}
                      >
                        <Minus size={14} aria-hidden="true" />
                      </button>
                      <strong>{d.maxCapaciteit}</strong>
                      <button
                        type="button"
                        aria-label={t("relatieDetailView.pleeggezin.cap.plus")}
                        disabled={!canEdit}
                        onClick={() => zetMax(d.diersoortId, d.maxCapaciteit + 1)}
                      >
                        <Plus size={14} aria-hidden="true" />
                      </button>
                    </span>
                  </div>
                  <div className="fawkes-pleeg-cap__stat">
                    <span className="fawkes-pleeg-cap__label">{t("relatieDetailView.pleeggezin.cap.huidig")}</span>
                    <strong>{huidig}</strong>
                  </div>
                  <div className="fawkes-pleeg-cap__stat">
                    <span className="fawkes-pleeg-cap__label">{t("relatieDetailView.pleeggezin.cap.vrij")}</span>
                    <strong>{vrij}</strong>
                  </div>
                </div>
                {d.categorieen.length > 0 && (
                  <>
                    <span className="fawkes-pleeg-cap__welkom-label">{t("relatieDetailView.pleeggezin.welkom")}</span>
                    <div className="fawkes-pleeg-cap__categorieen">
                      {d.categorieen.map((c) => (
                        <button
                          key={c.opvangcategorieId}
                          type="button"
                          role="switch"
                          aria-checked={c.welkom}
                          disabled={!canEdit}
                          className={`fawkes-pleeg-cap__cat${c.welkom ? " is-on" : ""}`}
                          onClick={() => toggleWelkom(d.diersoortId, c.opvangcategorieId, !c.welkom)}
                        >
                          <span className="fawkes-pleeg-cap__knop" aria-hidden="true" />
                          {c.naam}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </section>
            );
          })}
          {/* Jmix spells out that "Huidig aanwezig" counts the active branch only — without it the
              number reads as the foster family's total and the free-space sum looks wrong. */}
          <p className="fawkes-relatie-rollen__hint">{t("relatieDetailView.pleeggezin.cap.voet")}</p>
        </div>
      )}
    </>
  );
}

/**
 * Current or historic foster placements. Jmix lists these as animal cards — photo, name with its
 * species chip, branch, intake reason, the period and how long it lasted — with a summary row above
 * them, not as a five-column table. Same card shape as the Verblijven tile, so an animal reads the
 * same wherever it is listed.
 */
function PleeggezinPlaatsingenPanel({
  relatieId,
  sub,
  emptyKey,
  historisch,
  onOpenDier,
}: {
  relatieId: string;
  sub: string;
  emptyKey: string;
  historisch?: boolean;
  onOpenDier?: (dierId: string) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useTile<PleeggezinPlaatsing[]>(relatieId, sub);
  const rows = data ?? [];
  const opgehaald = dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null;
  // Ending a placement is a stay write, so it rides the same authority the stay endpoints enforce.
  const { data: permissions } = usePermissions<string[]>();
  const magRetour = !historisch && Array.isArray(permissions) && permissions.includes("ROLE_ANIMAL_STAY_EDIT");
  const { mutate: retourneer, isLoading: retourBezig } = useCustomMutation();
  const [retour, setRetour] = useState<PleeggezinPlaatsing | null>(null);
  const [retourFout, setRetourFout] = useState(false);
  const [vestiging, setVestiging] = useState("");

  const bevestigRetour = () => {
    if (!retour?.dierId) return;
    retourneer(
      {
        url: `animals/${retour.dierId}/foster/end`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => { setRetour(null); setRetourFout(false); refetch(); },
        onError: () => setRetourFout(true),
      },
    );
  };
  const fotoUrls = useAnimalPhotoUrls(rows.map((r) => r.dierId).filter((id): id is number => id != null));

  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;
  if (rows.length === 0) return <p className="fawkes-tile-placeholder">{t(emptyKey)}</p>;

  const vestigingen = [...new Set(rows.map((r) => r.vestiging).filter((v): v is string => !!v))].sort();
  const zichtbaar = vestiging ? rows.filter((r) => r.vestiging === vestiging) : rows;

  // Per species, the way Jmix heads this panel ("Aanwezig 5 · Hond 5").
  const perSoort = new Map<string, number>();
  for (const r of rows) {
    const soort = r.diersoort ?? "—";
    perSoort.set(soort, (perSoort.get(soort) ?? 0) + 1);
  }
  const laatste = rows
    .map((r) => r.totDatum ?? r.vanDatum)
    .filter((d): d is string => !!d)
    .sort()
    .at(-1);

  return (
    <>
      <div className="fawkes-relatie-facturen__totals fawkes-pleeg-stats">
        <div className="fawkes-relatie-facturen__total">
          <span className="fawkes-relatie-facturen__total-label">
            {t(historisch ? "relatieDetailView.pleeggezin.totaalVerblijven" : "relatieDetailView.pleeggezin.aanwezig")}
          </span>
          <span className="fawkes-relatie-verblijf__actief">
            <span className="fawkes-relatie-facturen__total-value">{rows.length}</span>
            {!historisch && <StatusBadge tone="success">{t("relatieDetailView.verblijven.status.ACTIEF")}</StatusBadge>}
          </span>
        </div>
        {historisch ? (
          <div className="fawkes-relatie-facturen__total">
            <span className="fawkes-relatie-facturen__total-label">{t("relatieDetailView.pleeggezin.laatsteVerblijf")}</span>
            <span className="fawkes-relatie-facturen__total-value">{laatste ? formatDate(laatste, locale) : "—"}</span>
          </div>
        ) : (
          [...perSoort.entries()].slice(0, 2).map(([soort, aantal]) => (
            <div className="fawkes-relatie-facturen__total" key={soort}>
              <span className="fawkes-relatie-facturen__total-label">{soort}</span>
              <span className="fawkes-relatie-facturen__total-value">{aantal}</span>
            </div>
          ))
        )}
        {/* Jmix stempelt de kaart met het moment waarop de cijfers zijn opgehaald, achter een
            streepje onderin: wie het scherm open laat staan ziet zo hoe oud ze zijn. */}
        {!historisch && (
          <p className="fawkes-pleeg-stats__update">
            <CalendarDays size={14} aria-hidden="true" />
            {t("relatieDetailView.pleeggezin.laatsteUpdate", { args: [formatDateTime(opgehaald, locale)] })}
          </p>
        )}
      </div>

      {/* Jmix heads the list with a branch filter — "Alle vestigingen" or only the one the operator is
          working in. It only earns its place once the placements span more than one branch. */}
      {vestigingen.length > 1 && (
        <span className="fawkes-relatie-filterpil">
          <MapPin size={16} aria-hidden="true" />
          <select
            aria-label={t("relatieDetailView.verblijven.filter.vestiging")}
            value={vestiging}
            onChange={(e) => setVestiging(e.target.value)}
          >
            <option value="">{t("relatieDetailView.verblijven.filter.alleVestigingen")}</option>
            {vestigingen.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </span>
      )}

      <ul className="fawkes-relatie-verblijf__cards">
        {zichtbaar.map((r) => {
          const foto = r.dierId != null ? fotoUrls[String(r.dierId)] : undefined;
          const klikbaar = onOpenDier != null && r.dierId != null;
          return (
            <li
              key={r.id}
              className={`fawkes-relatie-verblijf__card${klikbaar ? " is-klikbaar" : ""}`}
              role={klikbaar ? "button" : undefined}
              tabIndex={klikbaar ? 0 : undefined}
              onClick={klikbaar ? () => onOpenDier?.(String(r.dierId)) : undefined}
              onKeyDown={
                klikbaar
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpenDier?.(String(r.dierId));
                      }
                    }
                  : undefined
              }
            >
              <span className="fawkes-relatie-verblijf__thumb">
                {foto ? <img src={foto} alt="" /> : <span aria-hidden="true">{diersoortEmoji(r.diersoort)}</span>}
              </span>
              <div className="fawkes-relatie-verblijf__body">
                <span className="fawkes-relatie-verblijf__title">
                  <strong>{r.dierNaam ?? "—"}</strong>
                  {r.diersoort && <StatusBadge tone="neutral">{r.diersoort}</StatusBadge>}
                  {r.categorie && <StatusBadge tone="info">{r.categorie}</StatusBadge>}
                </span>
                <span className="fawkes-relatie-verblijf__line">{r.vestiging ?? "—"}</span>
                <span className="fawkes-relatie-verblijf__line">{r.opnameSoort ?? "—"}</span>
                <span className="fawkes-relatie-verblijf__dates">
                  <CalendarDays size={14} aria-hidden="true" />
                  {formatDate(r.vanDatum, locale)}
                  <span aria-hidden="true">→</span>
                  <CalendarDays size={14} aria-hidden="true" />
                  {r.totDatum ? formatDate(r.totDatum, locale) : t("relatieDetailView.verblijven.heden")}
                </span>
                <span className="fawkes-relatie-verblijf__duur">
                  {t(
                    historisch
                      ? "relatieDetailView.pleeggezin.dagenVerbleven"
                      : r.aantalDagen === 1
                        ? "relatieDetailView.verblijven.dag"
                        : "relatieDetailView.verblijven.dagen",
                    { args: [r.aantalDagen] },
                  )}
                </span>
              </div>
              {magRetour && r.dierId != null && (
                <Button
                  variant="secondary"
                  className="fawkes-pleeggezin__retour"
                  disabled={retourBezig}
                  onClick={(e) => { e.stopPropagation(); setRetourFout(false); setRetour(r); }}
                >
                  <Undo2 size={14} aria-hidden="true" />
                  {t("relatieDetailView.pleeggezin.retour")}
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      {/* Jmix closes both lists with a line explaining what the day count means. */}
      <p className="fawkes-relatie-rollen__hint">
        {t(historisch
          ? "relatieDetailView.pleeggezin.historieVoet"
          : "relatieDetailView.pleeggezin.huidigeVoet")}
      </p>

      {/* Jmix ends a placement on a single click. A confirm sits in front of it here: the button
          repeats on every card and the SPA has no undo — restoring one means editing the dates back. */}
      <Modal
        open={retour !== null}
        title={t("relatieDetailView.pleeggezin.retour")}
        onClose={() => setRetour(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRetour(null)}>{t("common.cancel")}</Button>
            <Button onClick={bevestigRetour} disabled={retourBezig}>{t("relatieDetailView.pleeggezin.retour")}</Button>
          </>
        }
      >
        <p>{t("relatieDetailView.pleeggezin.retourBevestig", { args: [retour?.dierNaam ?? ""] })}</p>
        {retourFout && <p className="fawkes-error">{t("relatieDetailView.pleeggezin.retourFout")}</p>}
      </Modal>
    </>
  );
}

const AFWEZIGHEID_TYPE_KEY: Record<string, string> = {
  vacation: "relatieDetailView.pleeggezin.afw.type.vacation",
  sick: "relatieDetailView.pleeggezin.afw.type.sick",
  other: "relatieDetailView.pleeggezin.afw.type.other",
};

function AfwezigheidPanel({ relatieId }: { relatieId: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  // retry:false so a 503 (platform unreachable/stale) fails fast — otherwise react-query's default
  // 3× backoff keeps the tile on "Laden…" for ~7s before `isError` flips. Jmix shows the banner at once.
  const { data, isLoading, isError, refetch } = useTile<Afwezigheid[]>(relatieId, "pleeggezin/afwezigheden", { retry: false });
  // The platform-side write key — also fetched by the Profiel panel; CRUD is enabled only once present.
  const { data: profiel } = useTile<PleeggezinProfielData>(relatieId, "pleeggezin");
  const { mutate, isLoading: saving } = useCustomMutation();
  const [editingId, setEditingId] = useState<string | null>(null); // "new" | <absence id> | null
  const [form, setForm] = useState({ van: "", tot: "", type: "vacation" });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [notice, setNotice] = useState<null | "error">(null);

  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  // A 503 (platform unreachable/stale) surfaces as isError — the Jmix AC6 "unavailable" banner.
  if (isError) return <p className="fawkes-tile-placeholder">{t("relatieDetailView.pleeggezin.afw.unavailable")}</p>;
  const rows = data ?? [];
  const klantUuid = profiel?.klantUuid ?? null;
  const canWrite = !!klantUuid;

  // Status summary (parity with the Jmix "Momenteel" / "Volgende afwezigheid" cards). ISO date strings
  // compare lexicographically, so plain string comparison gives the right ordering.
  const today = todayIso();
  const current = rows.find((r) => r.van && r.tot && r.van <= today && today <= r.tot) ?? null;
  const next = rows
    .filter((r) => r.van && r.van > today)
    .sort((a, b) => (a.van ?? "").localeCompare(b.van ?? ""))[0] ?? null;

  const startAdd = () => { setForm({ van: "", tot: "", type: "vacation" }); setNotice(null); setEditingId("new"); };
  const startEdit = (r: Afwezigheid) => {
    setForm({ van: r.van ?? "", tot: r.tot ?? "", type: r.type ?? "vacation" });
    setNotice(null);
    setEditingId(r.id);
  };
  const submit = () => {
    const values = { van: form.van || null, tot: form.tot || null, type: form.type };
    const isNew = editingId === "new";
    mutate(
      isNew
        ? { url: `pleeggezinnen/${klantUuid}/afwezigheden`, method: "post", values }
        : { url: `pleeggezinnen/${klantUuid}/afwezigheden/${editingId}`, method: "put", values },
      { onSuccess: () => { setEditingId(null); refetch(); }, onError: () => setNotice("error") },
    );
  };
  const remove = (id: string) => {
    mutate(
      { url: `pleeggezinnen/${klantUuid}/afwezigheden/${id}`, method: "delete", values: {} },
      { onSuccess: () => { setConfirmDeleteId(null); refetch(); }, onError: () => setNotice("error") },
    );
  };

  const columns: Column<Afwezigheid>[] = [
    {
      header: t("relatieDetailView.verblijven.col.periode"),
      cell: (r) => `${formatDate(r.van, locale)} → ${formatDate(r.tot, locale)}`,
    },
    { header: t("relatieDetailView.verblijven.col.duur"), cell: (r) => t("relatieDetailView.verblijven.dagen", { args: [r.aantalDagen] }) },
    { header: t("relatieDetailView.pleeggezin.afw.col.type"), cell: (r) => t(AFWEZIGHEID_TYPE_KEY[r.type ?? "other"] ?? AFWEZIGHEID_TYPE_KEY.other) },
    {
      header: t("relatieDetailView.pleeggezin.afw.col.bron"),
      cell: (r) => (r.source === "person" ? t("relatieDetailView.pleeggezin.afw.bron.person") : t("relatieDetailView.pleeggezin.afw.bron.organization")),
    },
  ];
  if (canWrite) {
    columns.push({
      header: t("relatieDetailView.pleeggezin.afw.col.acties"),
      cell: (r) =>
        confirmDeleteId === r.id ? (
          <span className="fawkes-relatie-tile__row-actions">
            <Button variant="secondary" onClick={() => remove(r.id)} disabled={saving}>{t("relatieDetailView.pleeggezin.afw.verwijderen")}</Button>
            <Button variant="secondary" onClick={() => setConfirmDeleteId(null)} disabled={saving}>{t("common.cancel")}</Button>
          </span>
        ) : (
          <span className="fawkes-relatie-tile__row-actions">
            <Button variant="secondary" onClick={() => startEdit(r)}>{t("common.edit")}</Button>
            <Button variant="secondary" onClick={() => { setNotice(null); setConfirmDeleteId(r.id); }}>{t("relatieDetailView.pleeggezin.afw.verwijderen")}</Button>
          </span>
        ),
    });
  }

  return (
    <div>
      <div className="fawkes-form-grid">
        <ReadField
          label={t("relatieDetailView.pleeggezin.afw.momenteel")}
          value={current ? t("relatieDetailView.pleeggezin.afw.afwezig") : t("relatieDetailView.pleeggezin.afw.beschikbaar")}
        />
        <ReadField
          label={t("relatieDetailView.pleeggezin.afw.volgende")}
          value={next ? `${formatDate(next.van, locale)} → ${formatDate(next.tot, locale)}` : null}
        />
      </div>

      {canWrite && (
        <div className="fawkes-relatie-tile__panel-head">
          {editingId === null && (
            <Button variant="secondary" onClick={startAdd}>{t("relatieDetailView.pleeggezin.afw.toevoegen")}</Button>
          )}
        </div>
      )}

      {editingId !== null && (
        <>
          <div className="fawkes-form-grid">
            <FieldShell label={t("relatieDetailView.pleeggezin.afw.van")}>
              <DateField className="fui-field__input" value={form.van}
                onChange={(e) => setForm((f) => ({ ...f, van: e.target.value }))} />
            </FieldShell>
            <FieldShell label={t("relatieDetailView.pleeggezin.afw.tot")}>
              <DateField className="fui-field__input" value={form.tot}
                onChange={(e) => setForm((f) => ({ ...f, tot: e.target.value }))} />
            </FieldShell>
            <FieldShell label={t("relatieDetailView.pleeggezin.afw.col.type")}>
              <select className="fui-field__input fui-select" value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                {Object.keys(AFWEZIGHEID_TYPE_KEY).map((k) => (
                  <option key={k} value={k}>{t(AFWEZIGHEID_TYPE_KEY[k])}</option>
                ))}
              </select>
            </FieldShell>
          </div>
          {notice === "error" && <p className="fawkes-error">{t("relatieDetailView.pleeggezin.opslaanMislukt")}</p>}
          <div className="fawkes-relatie-tile__actions">
            <Button variant="primary" onClick={submit} disabled={saving}>{t("common.save")}</Button>
            <Button variant="secondary" onClick={() => { setEditingId(null); setNotice(null); }} disabled={saving}>{t("common.cancel")}</Button>
          </div>
        </>
      )}

      {rows.length === 0 ? (
        editingId === null && <p className="fawkes-tile-placeholder">{t("relatieDetailView.pleeggezin.afw.empty")}</p>
      ) : (
        <IndeelbareTabel tabelKey="relatie-tiles-afw" columns={columns} rows={rows} getRowKey={(r) => r.id} empty={t("relatieDetailView.pleeggezin.afw.empty")} />
      )}
      {notice === "error" && editingId === null && (
        <p className="fawkes-error">{t("relatieDetailView.pleeggezin.opslaanMislukt")}</p>
      )}
    </div>
  );
}

/**
 * "Dier plaatsen bij dit pleeggezin" — the Jmix dialog behind the button on Huidige dieren: the
 * animals this foster may take (open stay, welcomed species at that branch, not already placed),
 * searchable by name, one of them placed on Plaatsen.
 */
function DierPlaatsenDialog({
  open,
  relatieId,
  onClose,
  onGeplaatst,
}: {
  open: boolean;
  relatieId: string;
  onClose: () => void;
  onGeplaatst: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, refetch } = useCustom<PlaatsbaarDier[]>({
    url: `relaties/${relatieId}/pleeggezin/plaatsbare-dieren`,
    method: "get",
  });
  const kandidaten = Array.isArray(data?.data) ? data.data : [];
  const { mutate: plaats, isLoading: bezig } = useCustomMutation();
  const [zoek, setZoek] = useState("");
  const [fout, setFout] = useState(false);
  const fotoUrls = useAnimalPhotoUrls(kandidaten.map((k) => k.dierId));

  useEffect(() => {
    if (open) {
      setZoek("");
      setFout(false);
      void refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const term = zoek.trim().toLowerCase();
  const getoond = term
    ? kandidaten.filter((k) => (k.dierNaam ?? "").toLowerCase().includes(term))
    : kandidaten;

  // Jmix places straight from the row — there is no select-then-confirm, and the footer holds only
  // Annuleren.
  const plaatsDier = (dierId: number) => {
    setFout(false);
    plaats(
      {
        url: `relaties/${relatieId}/pleeggezin/plaatsingen`,
        method: "post",
        values: { dierId },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onGeplaatst(), onError: () => setFout(true) },
    );
  };

  return (
    <Modal
      open={open}
      title={t("relatieDetailView.pleeggezin.plaatsTitel")}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>}
    >
      <div className="fawkes-relatie-plaats">
        <p className="fawkes-relatie-plaats__uitleg">{t("relatieDetailView.pleeggezin.plaatsUitleg")}</p>
        {fout && <p className="fawkes-error">{t("relatieDetailView.pleeggezin.plaatsFout")}</p>}
        <input
          type="search"
          autoComplete="off"
          placeholder={t("relatieDetailView.pleeggezin.zoekDier")}
          aria-label={t("relatieDetailView.pleeggezin.zoekDier")}
          value={zoek}
          onChange={(e) => setZoek(e.target.value)}
        />
        {isLoading && <p className="fawkes-muted">{t("common.loading")}</p>}
        {!isLoading && kandidaten.length === 0 && (
          <p className="fawkes-tile-placeholder">{t("relatieDetailView.pleeggezin.geenPlaatsbaar")}</p>
        )}
        {!isLoading && kandidaten.length > 0 && getoond.length === 0 && (
          <p className="fawkes-tile-placeholder">{t("relatieDetailView.pleeggezin.geenZoekresultaat")}</p>
        )}
        <ul className="fawkes-relatie-plaats__lijst">
          {getoond.map((k) => {
            const foto = fotoUrls[String(k.dierId)];
            const dagen = dagenSinds(k.opnameDatum);
            return (
              <li key={k.dierId} className="fawkes-relatie-plaats__kaart">
                <span className="fawkes-relatie-verblijf__thumb">
                  {foto ? <img src={foto} alt="" /> : <span aria-hidden="true">{diersoortEmoji(k.diersoort)}</span>}
                </span>
                <div className="fawkes-relatie-verblijf__body">
                  <span className="fawkes-relatie-verblijf__title">
                    <strong>{k.dierNaam ?? "—"}</strong>
                    {k.diersoort && <StatusBadge tone="neutral">{k.diersoort}</StatusBadge>}
                  </span>
                  <span className="fawkes-relatie-verblijf__line">{k.vestiging ?? "—"}</span>
                  <span className="fawkes-relatie-verblijf__line">{k.opnameSoort ?? "—"}</span>
                  <span className="fawkes-relatie-verblijf__duur">
                    {dagen == null
                      ? formatDate(k.opnameDatum, locale)
                      : t("relatieDetailView.pleeggezin.dagenInOpvang", { args: [dagen] })}
                  </span>
                </div>
                <Button variant="secondary" disabled={bezig} onClick={() => plaatsDier(k.dierId)}>
                  <Plus size={14} aria-hidden="true" />
                  {t("relatieDetailView.pleeggezin.plaatsen")}
                </Button>
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
}

export function PleeggezinTab({
  relatieId,
  onOpenDier,
}: {
  relatieId: string;
  onOpenDier?: (dierId: string) => void;
}) {
  const t = useTranslate();
  const { data: permissions } = usePermissions<string[]>();
  const magPlaatsen = Array.isArray(permissions) && permissions.includes("ROLE_RELATIE_EDIT");
  const [plaatsOpen, setPlaatsOpen] = useState(false);
  // The panels fetch their own feed; a placement changes two of them, so they remount on this counter.
  const [versie, setVersie] = useState(0);
  return (
    <div className="fawkes-pleeggezin__kolommen">
      <Tile title={t("relatieDetailView.pleeggezin.profielTitle")}>
        <PleeggezinProfielPanel relatieId={relatieId} />
      </Tile>
      <Tile
        title={
          <span className="fawkes-relatie-tiletitel">
            {t("relatieDetailView.pleeggezin.huidigeTitle")}
            {magPlaatsen && (
              <button
                type="button"
                className="fawkes-relatie-tiletitel__actie fawkes-relatie-tiletitel__actie--primair"
                onClick={() => setPlaatsOpen(true)}
              >
                <Plus size={14} aria-hidden="true" />
                {t("relatieDetailView.pleeggezin.dierPlaatsen")}
              </button>
            )}
          </span>
        }
      >
        <PleeggezinPlaatsingenPanel
          key={`huidige-${versie}`}
          relatieId={relatieId}
          sub="pleeggezin/huidige"
          emptyKey="relatieDetailView.pleeggezin.huidigeEmpty"
          onOpenDier={onOpenDier}
        />
        <DierPlaatsenDialog
          open={plaatsOpen}
          relatieId={relatieId}
          onClose={() => setPlaatsOpen(false)}
          onGeplaatst={() => { setPlaatsOpen(false); setVersie((v) => v + 1); }}
        />
      </Tile>
      <Tile title={t("relatieDetailView.pleeggezin.afwTitle")}>
        <AfwezigheidPanel relatieId={relatieId} />
      </Tile>
      <Tile title={t("relatieDetailView.pleeggezin.historieTitle")}>
        <PleeggezinPlaatsingenPanel
          relatieId={relatieId}
          sub="pleeggezin/historie"
          emptyKey="relatieDetailView.pleeggezin.historieEmpty"
          historisch
          onOpenDier={onOpenDier}
        />
      </Tile>
    </div>
  );
}

// ---- Media & documenten ----------------------------------------------------------------------------
// Dezelfde opzet als de diertegel (ontwerp 2026-09): tabbladen Media / Documenten met aantallen, kaarten
// met een "…"-menu, een uploadvlak onderaan en het nieuwe toevoegvenster. Het token zit alleen in het
// geheugen, dus foto's en downloads gaan via een geauthenticeerde blob-fetch.
/** De foto in een kaart: opgehaald met het token, want een kale <img src> kan dat niet meesturen. */
function MediaThumb({ relatieId, file }: { relatieId: string; file: MediaFile }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file.image || !file.previewAvailable) return;
    let revoked: string | null = null;
    let cancelled = false;
    fetchRelatieMediaBlob(relatieId, file.id)
      .then((blob) => {
        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        revoked = objUrl;
        setUrl(objUrl);
      })
      .catch(() => {
        /* leave the icon fallback */
      });
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [relatieId, file.id, file.image, file.previewAvailable]);

  if (url) {
    return <img className="fawkes-media-card__thumb fawkes-relatie-media__thumb-img" src={url} alt={file.fileName ?? ""} />;
  }
  return (
    <span className="fawkes-relatie-media__thumb-icon" aria-hidden="true">
      <FileIcon width={28} height={28} />
    </span>
  );
}

/**
 * Eén bestand als kaart, net als op de diertegel: een foto vult de kaart (soorticoon linksboven, datum
 * linksonder), een document is een lichte kaart met een gekleurd bestandsplaatje, de naam en de datum.
 * Rechtsboven het "…"-menu. Bewerken ontbreekt: de relatie-api geeft geen versie of tag-id's mee.
 */
function RelatieMediaKaart({ relatieId, file, magBewerken, onOpen, onDelete }: {
  relatieId: string; file: MediaFile; magBewerken: boolean;
  onOpen: (file: MediaFile) => void; onDelete: (file: MediaFile) => void;
}) {
  const t = useTranslate();
  const foto = file.image || isImage(file.contentType);
  const video = isVideo(file.contentType);
  const beeld = foto || video;
  const soort = bestandsSoort(file.fileName, file.contentType);
  const kleur = soortKleur(soort) ?? "#6b7280";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const dicht = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", dicht);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", dicht); document.removeEventListener("keydown", esc); };
  }, [menuOpen]);
  const actie = (fn: () => void) => () => { setMenuOpen(false); fn(); };

  return (
    <div className={`wa-kaart${beeld ? " wa-kaart--beeld" : " wa-kaart--doc"}`} title={file.fileName ?? undefined}>
      <button type="button" className="wa-kaart__open" onClick={() => onOpen(file)}
        aria-label={`${t("animalDetailView.media.view")}: ${file.fileName ?? ""}`}>
        {beeld && <MediaThumb relatieId={relatieId} file={file} />}
        {!beeld && (
          <span className="wa-kaart__doc">
            <span className="wa-kaart__doc-rond" style={{ background: `color-mix(in srgb, ${kleur} 14%, #fff)` }}>
              <span className={`wa-kaart__doc-badge${soort.length > 3 ? " is-lang" : ""}`} style={{ background: kleur }}>{soort}</span>
            </span>
            <strong className="wa-kaart__doc-naam">{file.fileName ?? "—"}</strong>
            <span className="wa-kaart__doc-datum">{kaartDatum(file.uploadedAt)}</span>
          </span>
        )}
      </button>
      {beeld && (
        <>
          <span className="wa-kaart__soort" aria-hidden="true">{video ? <VideoGlyph /> : <ImageGlyph />}</span>
          <span className="wa-kaart__datum">{kaartDatum(file.uploadedAt)}</span>
        </>
      )}
      {file.tags.length > 0 && (
        <span className="wa-kaart__tags" aria-hidden="true">
          {file.tags.slice(0, 2).map((tag, i) => (
            <span className="fawkes-media-chip" key={`${tag.label}-${i}`} style={tagChipStyle(tag.color)}>{tag.label}</span>
          ))}
        </span>
      )}
      <div className="wa-kaart__menu" ref={menuRef}>
        <button type="button" className="wa-kaart__puntjes" aria-haspopup="menu" aria-expanded={menuOpen}
          aria-label={`${t("animalDetailView.media.actions")}: ${file.fileName ?? ""}`}
          onClick={() => setMenuOpen((o) => !o)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
          </svg>
        </button>
        {menuOpen && (
          <ul className="wa-kaart__lijst" role="menu">
            <li role="none"><button type="button" role="menuitem" onClick={actie(() => onOpen(file))}>
              {t("animalDetailView.media.view")}</button></li>
            <li role="none"><button type="button" role="menuitem"
              onClick={actie(() => { void downloadRelatieMedia(relatieId, file.id, file.fileName ?? "bestand"); })}>
              {t("relatieDetailView.media.download")}</button></li>
            {magBewerken && (
              <li role="none"><button type="button" role="menuitem" className="is-gevaar" onClick={actie(() => onDelete(file))}>
                {t("relatieDetailView.media.verwijderen")}</button></li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

export function MediaTile({ relatieId }: { relatieId: string }) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useTile<MediaFile[]>(relatieId, "media");
  const { data: permissions } = usePermissions<string[]>();
  const magBewerken = Array.isArray(permissions) && permissions.includes("ROLE_RELATIE_EDIT");
  const { data: tagData } = useCustom<{ id: number; label: string; color: string | null }[]>({
    url: `relaties/${relatieId}/media/tag-options`,
    method: "get",
    queryOptions: { enabled: magBewerken },
  });
  const catalogus = Array.isArray(tagData?.data) ? tagData.data : [];
  const [datumFilter, setDatumFilter] = useState<MediaDatumFilter>("alles");
  const [tagFilter, setTagFilter] = useState("");
  const [tabblad, setTabblad] = useState<"media" | "documenten" | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [gevallen, setGevallen] = useState<File[]>([]);
  const [teVerwijderen, setTeVerwijderen] = useState<MediaFile | null>(null);
  const [vergroot, setVergroot] = useState<MediaFile | null>(null);
  const [alleOpen, setAlleOpen] = useState(false);
  const [melding, setMelding] = useState<{ tekst: string; fout: boolean } | null>(null);

  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;
  const files = Array.isArray(data) ? data : [];

  const tagLabels = [...new Set(files.flatMap((f) => f.tags.map((tag) => tag.label)))].sort();
  const gefilterd = files
    .filter((f) => binnenMediaPeriode(f, datumFilter))
    .filter((f) => !tagFilter || f.tags.some((tag) => tag.label === tagFilter));
  const isBeeld = (f: MediaFile) => f.image || isImage(f.contentType) || isVideo(f.contentType);
  const media = gefilterd.filter(isBeeld);
  const documenten = gefilterd.filter((f) => !isBeeld(f));
  const actief = tabblad ?? (media.length > 0 || documenten.length === 0 ? "media" : "documenten");
  const lijst = (actief === "media" ? media : documenten)
    .slice()
    .sort((a, b) => (b.uploadedAt ?? "").localeCompare(a.uploadedAt ?? ""));

  const kaart = (f: MediaFile, uitVenster = false) => (
    <RelatieMediaKaart key={f.id} relatieId={relatieId} file={f} magBewerken={magBewerken}
      onOpen={(x) => { if (uitVenster) setAlleOpen(false); open(x); }}
      onDelete={(x) => { if (uitVenster) setAlleOpen(false); setMelding(null); setTeVerwijderen(x); }} />
  );

  const open = (f: MediaFile) => {
    if (f.image && f.previewAvailable) {
      setVergroot(f);
      return;
    }
    void downloadRelatieMedia(relatieId, f.id, f.fileName ?? "bestand");
  };
  const upload = async (bestanden: File[], omschrijving: string, tagIds: number[]) => {
    try {
      await uploadRelatieMedia(relatieId, bestanden, omschrijving, tagIds);
      setUploadOpen(false);
      setMelding({ tekst: t("relatieDetailView.media.geupload"), fout: false });
      void refetch();
    } catch {
      setMelding({ tekst: t("relatieDetailView.media.uploadFout"), fout: true });
    }
  };

  return (
    <div className="fawkes-relatie-tile fawkes-relatie-tile--fill fawkes-media">
      {melding && (
        <p className={melding.fout ? "fawkes-media__notice fawkes-media__notice--error" : "fawkes-media__notice"}>{melding.tekst}</p>
      )}
      <div className="wa-media__kop">
        <div className="wa-media__tabs" role="tablist" aria-label={t("animalDetailView.media.tabs")}>
          <button type="button" role="tab" aria-selected={actief === "media"}
            className={`wa-media__tab${actief === "media" ? " is-actief" : ""}`} onClick={() => setTabblad("media")}>
            {t("animalDetailView.media.tab.media")} <span className="wa-media__aantal">{media.length}</span>
          </button>
          <button type="button" role="tab" aria-selected={actief === "documenten"}
            className={`wa-media__tab${actief === "documenten" ? " is-actief" : ""}`} onClick={() => setTabblad("documenten")}>
            {t("animalDetailView.media.tab.documents")} <span className="wa-media__aantal">{documenten.length}</span>
          </button>
        </div>
        {magBewerken && (
          <button type="button" className="wa-media__plus"
            aria-label={t("relatieDetailView.media.toevoegen")} title={t("relatieDetailView.media.toevoegen")}
            onClick={() => { setMelding(null); setGevallen([]); setUploadOpen(true); }}>
            <Plus size={18} aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="wa-media__filters">
        <select className="wa-media__filter" value={datumFilter} aria-label={t("relatieDetailView.media.filter.datum")}
          onChange={(e) => setDatumFilter(e.target.value as MediaDatumFilter)}>
          {MEDIA_DATUM_FILTERS.map((k) => (
            <option key={k} value={k}>{t(`animalDetailView.media.filter.date.${k}`)}</option>
          ))}
        </select>
        {tagLabels.length > 0 && (
          <select className="wa-media__filter" value={tagFilter} aria-label={t("relatieDetailView.media.filter.tag")}
            onChange={(e) => setTagFilter(e.target.value)}>
            <option value="">{t("relatieDetailView.media.filter.alleTags")}</option>
            {tagLabels.map((label) => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>
        )}
      </div>

      {files.length === 0 && <p className="fawkes-relatie-media__leeg">{t("relatieDetailView.media.empty")}</p>}
      {files.length > 0 && lijst.length === 0 && (
        <p className="fawkes-relatie-media__leeg">{t("relatieDetailView.media.filterGeen")}</p>
      )}
      {lijst.length > 0 && (
        <div className="wa-media__kaarten">
          {lijst.slice(0, MEDIA_OP_TEGEL).map((f) => kaart(f))}
        </div>
      )}
      {lijst.length > MEDIA_OP_TEGEL && (
        <button type="button" className="wa-media__alles" onClick={() => setAlleOpen(true)}>
          {t("animalDetailView.media.showAll", { args: [String(lijst.length)] })}
        </button>
      )}
      <Modal
        open={alleOpen}
        size="wide"
        title={`${t(actief === "media" ? "animalDetailView.media.tab.media" : "animalDetailView.media.tab.documents")} (${lijst.length})`}
        onClose={() => setAlleOpen(false)}
        footer={<Button variant="secondary" onClick={() => setAlleOpen(false)}>{t("common.close")}</Button>}
      >
        <div className="wa-media__kaarten wa-media__kaarten--alles">
          {lijst.map((f) => kaart(f, true))}
        </div>
      </Modal>
      {magBewerken && (
        <button
          type="button"
          className="wa-media__drop"
          onClick={() => { setMelding(null); setGevallen([]); setUploadOpen(true); }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); setMelding(null); setGevallen(Array.from(e.dataTransfer.files ?? [])); setUploadOpen(true); }}
        >
          <UploadCloudIcon width={30} height={30} aria-hidden="true" />
          <strong>{t("animalDetailView.media.dropTitle")}</strong>
          <span>{t("animalDetailView.media.dropHint")}</span>
        </button>
      )}

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} catalog={catalogus}
        onUpload={upload} initialFiles={gevallen} metPleeggezin={false} t={t} />

      <Modal
        open={!!teVerwijderen}
        title={t("relatieDetailView.media.verwijderTitel")}
        onClose={() => setTeVerwijderen(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTeVerwijderen(null)}>{t("common.cancel")}</Button>
            <Button
              onClick={async () => {
                const file = teVerwijderen;
                if (!file) return;
                setTeVerwijderen(null);
                try {
                  await deleteRelatieMedia(relatieId, file.id);
                  setMelding({ tekst: t("relatieDetailView.media.verwijderd"), fout: false });
                  void refetch();
                } catch {
                  setMelding({ tekst: t("relatieDetailView.media.verwijderFout"), fout: true });
                }
              }}
            >
              {t("common.delete")}
            </Button>
          </>
        }
      >
        <p>
          {t("relatieDetailView.media.verwijderBevestiging")}
          {teVerwijderen?.fileName ? ` (${teVerwijderen.fileName})` : ""}
        </p>
      </Modal>

      {/* Een foto vergroot in de app, zoals op de diertegel; downloaden kan vanuit het menu. */}
      <Modal
        open={!!vergroot}
        title={vergroot?.fileName ?? ""}
        size="wide"
        onClose={() => setVergroot(null)}
        footer={
          <>
            <Button variant="secondary"
              onClick={() => { if (vergroot) void downloadRelatieMedia(relatieId, vergroot.id, vergroot.fileName ?? "bestand"); }}>
              <DownloadIcon width={16} height={16} /> {t("relatieDetailView.media.download")}
            </Button>
            <Button variant="secondary" onClick={() => setVergroot(null)}>{t("common.close")}</Button>
          </>
        }
      >
        {vergroot && (
          <div className="fawkes-relatie-media__vergroot">
            <MediaThumb relatieId={relatieId} file={vergroot} />
          </div>
        )}
      </Modal>
    </div>
  );
}

// ---- Crematorium profiles (Dierenarts / Derde-partij) ---------------------------------------------
// Read-only port of the Jmix CrematieRelatiePrijsPanel "Huidige prijsafspraken": changeset 369 unified
// both roles onto the versioned crematie_relatie_prijs_versie table, so the api serves the ACTIVE price
// version with the same field set for both tabs. Role-gated tabs (DIERENARTSPRAKTIJK / DERDE_PARTIJ)
// under the crematorium module; the module gate has no api signal yet, so role-presence is the gate
// (surface, don't fake). Writes (the version history + CRUD) deferred.
interface CrematieProfielData {
  heeftProfiel: boolean;
  vergoeding: number | null;
  vervoersprijs: number | null;
  btwPercentage: number | null;
  ingangsdatum: string | null;
  interneOpmerking: string | null;
}

/** One price agreement of a crematorium role, as the "Prijswijzigingen" table lists it. */
interface CrematiePrijsVersie {
  id: number;
  vergoeding: number | null;
  vervoersprijs: number | null;
  btwPercentage: number | null;
  ingangsdatum: string | null;
  interneOpmerking: string | null;
  gewijzigdDoor: string | null;
  status: string | null;
  version: number | null;
}

const CREMATIE_STATUS_TOON: Record<string, StatusTone> = {
  ACTIEF: "success",
  GEPLAND: "info",
  VERVALLEN: "neutral",
};

/** The add/edit form of a price change — the Jmix "Nieuwe prijswijziging" / "Prijswijziging wijzigen". */
function CrematiePrijsDialog({
  open,
  bestaand,
  onClose,
  onOpslaan,
  bezig,
}: {
  open: boolean;
  bestaand: CrematiePrijsVersie | null;
  onClose: () => void;
  onOpslaan: (waarden: Record<string, unknown>) => void;
  bezig: boolean;
}) {
  const t = useTranslate();
  const [form, setForm] = useState({ vergoeding: "", vervoersprijs: "", btw: "21", ingangsdatum: todayIso(), opmerking: "" });

  useEffect(() => {
    if (open) {
      setForm({
        vergoeding: bestaand?.vergoeding != null ? String(bestaand.vergoeding) : "",
        vervoersprijs: bestaand?.vervoersprijs != null ? String(bestaand.vervoersprijs) : "",
        btw: bestaand?.btwPercentage != null ? String(bestaand.btwPercentage) : "21",
        ingangsdatum: bestaand?.ingangsdatum ?? todayIso(),
        opmerking: bestaand?.interneOpmerking ?? "",
      });
    }
  }, [open, bestaand]);

  const getal = (v: string) => (v.trim() === "" ? null : Number(v.replace(",", ".")));

  return (
    <Modal
      open={open}
      title={t(bestaand ? "relatieDetailView.crematie.wijzigTitel" : "relatieDetailView.crematie.nieuwTitel")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            disabled={bezig || form.ingangsdatum === ""}
            onClick={() =>
              onOpslaan({
                vergoeding: getal(form.vergoeding),
                vervoersprijs: getal(form.vervoersprijs),
                btwPercentage: getal(form.btw),
                ingangsdatum: form.ingangsdatum,
                interneOpmerking: form.opmerking.trim() === "" ? null : form.opmerking.trim(),
                version: bestaand?.version ?? null,
              })
            }
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="fawkes-relatie-grid fawkes-relatie-grid--2">
        <VeldCel
          label={t("relatieDetailView.crematie.vergoeding")}
          value={form.vergoeding}
          onChange={(e) => setForm((f) => ({ ...f, vergoeding: e.target.value }))}
        />
        <VeldCel
          label={t("relatieDetailView.crematie.vervoersprijs")}
          value={form.vervoersprijs}
          onChange={(e) => setForm((f) => ({ ...f, vervoersprijs: e.target.value }))}
        />
        <VeldCel
          label={t("relatieDetailView.crematie.btw")}
          value={form.btw}
          onChange={(e) => setForm((f) => ({ ...f, btw: e.target.value }))}
        />
        <VeldCel
          label={t("relatieDetailView.crematie.ingangsdatum")}
          type="date"
          value={form.ingangsdatum}
          onChange={(e) => setForm((f) => ({ ...f, ingangsdatum: e.target.value }))}
        />
        <VeldCel
          span={2}
          label={t("relatieDetailView.crematie.interneOpmerking")}
          value={form.opmerking}
          onChange={(e) => setForm((f) => ({ ...f, opmerking: e.target.value }))}
        />
      </div>
    </Modal>
  );
}

function CrematieProfielTab({ relatieId, sub, emptyKey }: { relatieId: string; sub: string; emptyKey: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError, refetch } = useTile<CrematieProfielData>(relatieId, sub);
  const { data: versieData, refetch: refetchVersies } = useCustom<CrematiePrijsVersie[]>({
    url: `relaties/${relatieId}/crematie-prijzen/${sub}`,
    method: "get",
  });
  const versies = Array.isArray(versieData?.data) ? versieData.data : [];
  const { data: permissions } = usePermissions<string[]>();
  const magBewerken = Array.isArray(permissions) && permissions.includes("ROLE_RELATIE_EDIT");
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [dialoog, setDialoog] = useState<{ open: boolean; versie: CrematiePrijsVersie | null }>({ open: false, versie: null });
  const [teVerwijderen, setTeVerwijderen] = useState<CrematiePrijsVersie | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const herlaad = () => { void refetch(); void refetchVersies(); };
  // The badge on the current agreement: ACTIEF unless every version is still in the future.
  const huidigeStatus = versies.some((v) => v.status === "ACTIEF") ? "ACTIEF" : "GEPLAND";

  const opslaan = (waarden: Record<string, unknown>) => {
    const versie = dialoog.versie;
    setFout(null);
    mutate(
      {
        url: versie
          ? `relaties/${relatieId}/crematie-prijzen/${sub}/${versie.id}`
          : `relaties/${relatieId}/crematie-prijzen/${sub}`,
        method: versie ? "put" : "post",
        values: waarden,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => { setDialoog({ open: false, versie: null }); herlaad(); },
        onError: () => setFout(t("relatieDetailView.crematie.opslaanFout")),
      },
    );
  };

  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;
  const d = data;

  const kolommen: Column<CrematiePrijsVersie>[] = [
    { header: t("relatieDetailView.crematie.col.datum"), cell: (r) => formatDate(r.ingangsdatum, locale) },
    { header: t("relatieDetailView.crematie.vergoeding"), cell: (r) => (r.vergoeding != null ? formatEuro(r.vergoeding, locale) : "—") },
    { header: t("relatieDetailView.crematie.vervoersprijs"), cell: (r) => (r.vervoersprijs != null ? formatEuro(r.vervoersprijs, locale) : "—") },
    { header: t("relatieDetailView.crematie.btw"), cell: (r) => (r.btwPercentage != null ? `${r.btwPercentage}%` : "—") },
    { header: t("relatieDetailView.crematie.gewijzigdDoor"), cell: (r) => r.gewijzigdDoor ?? "—" },
    {
      header: t("relatieDetailView.crematie.status"),
      cell: (r) => (
        <StatusBadge tone={CREMATIE_STATUS_TOON[r.status ?? ""] ?? "neutral"}>
          {t(`relatieDetailView.crematie.status.${r.status ?? "VERVALLEN"}`)}
        </StatusBadge>
      ),
    },
    ...(magBewerken
      ? [{
          header: "",
          cell: (r: CrematiePrijsVersie) => (
            <span className="fawkes-relatie-prijs__acties">
              <Button variant="secondary" onClick={() => { setFout(null); setDialoog({ open: true, versie: r }); }}>
                {t("relatieDetailView.crematie.wijzigen")}
              </Button>
              <Button variant="secondary" onClick={() => { setFout(null); setTeVerwijderen(r); }}>
                {t("common.delete")}
              </Button>
            </span>
          ),
        }]
      : []),
  ];

  return (
    <div className="fawkes-relatie-tile">
      {/* The agreement in force — what the Jmix panel heads the tab with, badge and all. */}
      <div className="fawkes-relatie-prijs__kop">
        <h3 className="fawkes-relatie-prijs__titel">{t("relatieDetailView.crematie.huidige")}</h3>
        {d && d.heeftProfiel && (
          <StatusBadge tone={CREMATIE_STATUS_TOON[huidigeStatus] ?? "neutral"}>
            {t(`relatieDetailView.crematie.status.${huidigeStatus}`)}
          </StatusBadge>
        )}
      </div>
      {d && d.heeftProfiel ? (
        <div className="fawkes-form-grid">
          <ReadField label={t("relatieDetailView.crematie.vergoeding")} value={d.vergoeding != null ? formatEuro(d.vergoeding, locale) : null} />
          <ReadField label={t("relatieDetailView.crematie.vervoersprijs")} value={d.vervoersprijs != null ? formatEuro(d.vervoersprijs, locale) : null} />
          <ReadField label={t("relatieDetailView.crematie.btw")} value={d.btwPercentage != null ? `${d.btwPercentage}%` : null} />
          <ReadField label={t("relatieDetailView.crematie.ingangsdatum")} value={formatDate(d.ingangsdatum, locale)} />
          <ReadField label={t("relatieDetailView.crematie.interneOpmerking")} value={d.interneOpmerking} span2 />
        </div>
      ) : (
        <p className="fawkes-tile-placeholder">{t(emptyKey)}</p>
      )}

      {/* And the whole history of agreements, which is where they are made and changed. */}
      <div className="fawkes-relatie-tegelkop fawkes-relatie-prijs__kop">
        <h3 className="fawkes-relatie-prijs__titel">{t("relatieDetailView.crematie.prijswijzigingen")}</h3>
        {magBewerken && (
          <Button variant="secondary" onClick={() => { setFout(null); setDialoog({ open: true, versie: null }); }}>
            <Plus size={14} aria-hidden="true" />
            {t("relatieDetailView.crematie.nieuweWijziging")}
          </Button>
        )}
      </div>
      {fout && <p className="fawkes-error">{fout}</p>}
      <IndeelbareTabel tabelKey="relatie-tiles-geen-wijzigingen"
        columns={kolommen}
        rows={versies}
        getRowKey={(r) => String(r.id)}
        empty={t("relatieDetailView.crematie.geenWijzigingen")}
      />

      <CrematiePrijsDialog
        open={dialoog.open}
        bestaand={dialoog.versie}
        bezig={bezig}
        onClose={() => setDialoog({ open: false, versie: null })}
        onOpslaan={opslaan}
      />

      <Modal
        open={!!teVerwijderen}
        title={t("relatieDetailView.crematie.verwijderTitel")}
        onClose={() => setTeVerwijderen(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTeVerwijderen(null)}>{t("common.cancel")}</Button>
            <Button
              onClick={() => {
                const versie = teVerwijderen;
                if (!versie) return;
                setTeVerwijderen(null);
                mutate(
                  {
                    url: `relaties/${relatieId}/crematie-prijzen/${sub}/${versie.id}`,
                    method: "delete",
                    values: {},
                    successNotification: false,
                    errorNotification: false,
                  },
                  { onSuccess: herlaad, onError: () => setFout(t("relatieDetailView.crematie.verwijderFout")) },
                );
              }}
            >
              {t("common.delete")}
            </Button>
          </>
        }
      >
        <p>{t("relatieDetailView.crematie.verwijderBevestiging")}</p>
      </Modal>
    </div>
  );
}

export function DierenartsTab({ relatieId }: { relatieId: string }) {
  return <CrematieProfielTab relatieId={relatieId} sub="dierenarts" emptyKey="relatieDetailView.dierenarts.geen" />;
}

export function DerdePartijTab({ relatieId }: { relatieId: string }) {
  return <CrematieProfielTab relatieId={relatieId} sub="derde-partij" emptyKey="relatieDetailView.derdePartij.geen" />;
}

// ---- Geschiedenis ----------------------------------------------------------------------------------
// Dutch labels for the audit event codes the Relaties Geschiedenis renders — a faithful subset of the
// Jmix RelatieGeschiedenisPanel mapping (pleeggezin/klant/donatie/profiel families). Unknown codes
// fall back to a humanised form of the raw code.
const EVENT_LABELS: Record<string, string> = {
  KLANT_CREATED: "Relatie aangemaakt",
  KLANT_UPDATED: "Relatie bijgewerkt",
  RELATIE_SAMENGEVOEGD: "Samengevoegd met een andere relatie",
  RELATIE_OVERGENOMEN: "Een andere relatie is hierin opgegaan",
  PLEEGGEZIN_PROFIEL_CREATED: "Pleeggezin-profiel aangemaakt",
  PLEEGGEZIN_PROFIEL_UPDATED: "Pleeggezin-profiel bijgewerkt",
  PLEEGGEZIN_CAPACITEIT_UPDATED: "Capaciteit gewijzigd",
  PLEEGGEZIN_WELKOM_ADDED: "Welkom-categorie toegevoegd",
  PLEEGGEZIN_WELKOM_REMOVED: "Welkom-categorie verwijderd",
  PLEEGGEZIN_AFWEZIGHEID_CREATED: "Afwezigheid toegevoegd",
  PLEEGGEZIN_AFWEZIGHEID_UPDATED: "Afwezigheid bijgewerkt",
  PLEEGGEZIN_AFWEZIGHEID_DELETED: "Afwezigheid verwijderd",
  PLEEGGEZIN_VERBLIJF_PLAATSING: "Dier geplaatst bij pleeggezin",
  PLEEGGEZIN_VERBLIJF_EINDE: "Dier teruggehaald",
  PLEEGGEZIN_VERBLIJF_UPDATED: "Verblijfsperiode aangepast",
  PLEEGGEZIN_VERBLIJF_DELETED: "Verblijf verwijderd",
  DONATION_CREATED: "Donatie geregistreerd",
  RECURRING_CREATED: "Periodieke donatie aangemaakt",
  RECURRING_UPDATED: "Periodieke donatie bijgewerkt",
  RECURRING_PAUSED: "Periodieke donatie gepauzeerd",
  RECURRING_STOPPED: "Periodieke donatie gestopt",
  PROFILE_CREATED: "Donateurschap aangemaakt",
  PROFILE_UPDATED: "Donateurschap bijgewerkt",
  PROFILE_STOPPED: "Donateurschap gestopt",
  PROFILE_REACTIVATED: "Donateurschap heractiveerd",
  KLANT_DELETED: "Relatie verwijderd",
};

/** The entity names Jmix spells out in the feed; anything else keeps its own name. */
const ENTITEIT_LABELS: Record<string, string> = {
  DIER: "Dier",
  ASIELVERBLIJF: "Asielverblijf",
  ASIELVERBLIJFNOTITIE: "Notitie",
  ASIELVERBLIJFSTATUSPERIODE: "Statusperiode",
  ASIELVERBLIJFOPVANGCATEGORIE: "Opvangcategorie",
  MEETMOMENTHOND: "Meetmoment",
  MEETMOMENTKAT: "Meetmoment",
  TNRVANGACTIEDIER: "TNR-vangactie",
  DOSSIERREGEL: "Dossierregel",
  USERANIMALPREFERENCE: "Voorkeur",
};

const ACTIE_LABELS: Record<string, string> = {
  CREATED: "aangemaakt",
  UPDATED: "bijgewerkt",
  DELETED: "verwijderd",
};

/**
 * What happened, in Dutch — the Jmix {@code eventLabel}. Known event types have their own wording;
 * everything else is an {@code <ENTITEIT>_<ACTIE>} pair, so "FACTUUR_CREATED" reads "Factuur
 * aangemaakt" rather than the raw "Factuur created" this used to show.
 */
function eventTitle(eventType: string): string {
  const known = EVENT_LABELS[eventType];
  if (known) return known;
  const scheiding = eventType.lastIndexOf("_");
  if (scheiding > 0) {
    const actie = ACTIE_LABELS[eventType.slice(scheiding + 1).toUpperCase()];
    if (actie) {
      const entiteit = eventType.slice(0, scheiding).toUpperCase();
      const naam = ENTITEIT_LABELS[entiteit]
        ?? entiteit.charAt(0) + entiteit.slice(1).toLowerCase();
      return `${naam} ${actie}`;
    }
  }
  return eventType
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** Bookkeeping columns Jmix keeps out of the feed — they say nothing about what a user did. */
const VERBORGEN_VELDEN = new Set([
  "dier", "klant", "version", "uuid", "createdat", "createdby", "updatedat", "updatedby",
]);

/** "factuurDatum" → "Factuur datum" (the Jmix humanize). */
function humaniseerVeld(sleutel: string): string {
  const woorden = sleutel
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return woorden.charAt(0).toUpperCase() + woorden.slice(1);
}

/**
 * The audit payload as Jmix shows it: "Factuur datum: '2026-08-03' · Factuur groep: '8052' · …".
 * The raw form is `key: 'value'` pairs separated by semicolons, with entity references written as
 * `Id{nl.docasoft…Klant, 42}`; the FQN is stripped and the bookkeeping columns are dropped. Anything
 * that does not parse as pairs (a short "dier=Beertje", a change list) is left as it is.
 */
function formatPayload(payload: string): string {
  const ruw = payload.trim();
  if (ruw === "" || !/[:=]/.test(ruw)) return ruw;
  const delen = ruw
    .split(";")
    .map((deel) => deel.trim())
    .filter((deel) => deel !== "");
  const uit: string[] = [];
  for (const deel of delen) {
    // Both notations occur: "factuurDatum: '…'" from the audit trail and "dier=Beertje" from the
    // foster events. Whichever separator comes first is the one.
    const dubbelePunt = deel.indexOf(":");
    const isGelijk = deel.indexOf("=");
    const scheiding = dubbelePunt < 1 ? isGelijk
      : isGelijk < 1 ? dubbelePunt
        : Math.min(dubbelePunt, isGelijk);
    if (scheiding < 1) return ruw;
    const sleutel = deel.slice(0, scheiding).trim();
    if (VERBORGEN_VELDEN.has(sleutel.toLowerCase())) continue;
    const waarde = deel
      .slice(scheiding + 1)
      .trim()
      .replace(/Id\{[\w.]+\.([A-Z]\w+),\s*([^}]+)\}/g, "$1#$2")
      .replace(/(?:[a-z]\w*\.){2,}([A-Z]\w+)/g, "$1");
    uit.push(`${humaniseerVeld(sleutel)}: ${waarde}`);
  }
  return uit.join(" · ");
}

/** The feed by day, newest day first — the grouping Jmix heads each block with. */
function groepeerPerDag(events: RelatieEvent[]): [string, RelatieEvent[]][] {
  const perDag = new Map<string, RelatieEvent[]>();
  for (const e of events) {
    const dag = (e.occurredAt ?? "").slice(0, 10);
    const rij = perDag.get(dag);
    if (rij) rij.push(e);
    else perDag.set(dag, [e]);
  }
  return [...perDag.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}

/** "3 AUGUSTUS 2026" — the Jmix day heading. */
function dagKop(dag: string, locale: string): string {
  if (!dag) return "—";
  const d = new Date(`${dag}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dag;
  return d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" }).toUpperCase();
}

function tijdVan(iso: string | null, locale: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

/**
 * One history card: pencil, what happened, the details clamped to a few lines behind "Toon meer",
 * and the time + user underneath — the Jmix card, field for field.
 */
function HistorieKaart({
  event,
  locale,
  t,
}: {
  event: RelatieEvent;
  locale: string;
  t: (key: string) => string;
}) {
  const [detail, setDetail] = useState(false);
  const titel = eventTitle(event.eventType);
  const payload = formatPayload(event.payload ?? "");
  // Only worth the link once there is more than the clamp shows; the number matches the CSS clamp.
  const lang = payload.length > 120;
  return (
    <div className="fawkes-relatie-history__event">
      <span className="fawkes-relatie-history__icoon" aria-hidden="true">
        <Pencil size={14} />
      </span>
      <div className="fawkes-relatie-history__body">
        <span className="fawkes-relatie-history__title">{titel}</span>
        {payload !== "" && (
          <span className={`fawkes-relatie-history__payload${lang ? " is-ingekort" : ""}`}>{payload}</span>
        )}
        {/* Jmix opens the whole payload in a dialog rather than growing the card. */}
        {lang && (
          <button type="button" className="fawkes-relatie-history__meer" onClick={() => setDetail(true)}>
            {t("relatieDetailView.history.toonMeer")}
          </button>
        )}
        <span className="fawkes-relatie-history__meta">
          {tijdVan(event.occurredAt, locale)}
          {event.userId ? ` · ${event.userId}` : ` · ${t("relatieDetailView.history.systeem")}`}
        </span>
      </div>
      <Modal
        open={detail}
        title={titel}
        onClose={() => setDetail(false)}
        footer={<Button variant="secondary" onClick={() => setDetail(false)}>{t("common.close")}</Button>}
      >
        <p className="fawkes-relatie-history__detail">{payload}</p>
      </Modal>
    </div>
  );
}

export function GeschiedenisTab({ relatieId }: { relatieId: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [query, setQuery] = useState("");
  const { data, isLoading, isError } = useTile<RelatieEvent[]>(relatieId, "history");

  if (isLoading || isError) return <Status isLoading={isLoading} isError={isError} t={t} />;

  const all = data ?? [];
  const q = query.trim().toLowerCase();
  const filtered =
    q === ""
      ? all
      : all.filter((e) =>
          `${eventTitle(e.eventType)} ${e.payload ?? ""} ${e.userId ?? ""}`.toLowerCase().includes(q),
        );

  return (
    <div className="fawkes-history">
      <div className="fawkes-history__search-wrap">
        <SearchIcon className="fawkes-history__search-icon" aria-hidden="true" />
        <input
          className="fawkes-history__search"
          type="search"
          autoComplete="off"
          value={query}
          onChange={(ev) => setQuery(ev.target.value)}
          placeholder={t("relatieDetailView.history.search")}
          aria-label={t("relatieDetailView.history.search")}
        />
      </div>
      <div className="fawkes-history__content">
        {all.length === 0 ? (
          <p className="fawkes-tile-placeholder">{t("relatieDetailView.history.empty")}</p>
        ) : filtered.length === 0 ? (
          <p className="fawkes-tile-placeholder">{t("relatieDetailView.history.emptyFiltered")}</p>
        ) : (
          // Jmix groups the feed by day under a date heading, and each card carries the time + user
          // rather than repeating the date.
          groepeerPerDag(filtered).map(([dag, gebeurtenissen]) => (
            <section className="fawkes-relatie-history__dag" key={dag}>
              <h3 className="fawkes-relatie-history__dagkop">{dagKop(dag, locale)}</h3>
              <div className="fawkes-history__items">
                {gebeurtenissen.map((e) => (
                  <HistorieKaart key={e.id} event={e} locale={locale} t={t} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
